/**
 * buildCostControlData.js
 * Shared utility — used by CostControl and AnalyticsStudio
 *
 * mode: 'cumulative' → returns flat array of cost code rows (same as CostControl)
 * mode: 'by_month'   → returns { periods:[], rows:[] } using actual_cost_date
 */
import { supabase } from './supabase'

// ─── Cumulative (CostControl logic) ──────────────────────────────────────────
// asOfForecastDate (optional, 5th param): the ONLY way a caller can request
// EAC/ETC values as of a specific forecast_date. Phase 2 removed the old
// "globally active forecast period" concept (forecast_active_date) entirely
// — this function no longer reads that table or infers any forecast context
// from mutable global state. Behavior is now fully deterministic from
// explicit inputs:
//   - asOfForecastDate omitted/null → plain live financial state: every
//     code resolves through the Not-Forecasted Budget fallback (as if no
//     forecast existed at all).
//   - asOfForecastDate supplied (a Draft's or a Revision's own forecast_date,
//     provided explicitly by the caller) → EAC/ETC for that exact forecast
//     context, same pipeline, same Ownership/Budget-fallback rules.
// This reuses this exact same function and pipeline — no second calculation
// engine, no duplicated financial logic, and no hidden coupling to whichever
// period happened to be "active" elsewhere in the app.
//
// IMPORTANT — documented, intentional, temporary limitation:
// Only Forecast/EAC (and metrics derived from it: VAC, CPI, EV-based variance,
// Gross Profit trend, Forecast Change) are truly historical when this parameter
// is passed. BAC, Commitment, and Actual Cost have NO historical snapshot
// mechanism yet (forecast_history is currently the only table with a real,
// verified per-period snapshot design). Those three still reflect current/
// cumulative-to-date values regardless of asOfForecastDate.
// This is a data-model limitation, not an architectural one — when historical
// snapshots for Actual/Commitment/BAC are eventually introduced (e.g. a
// budget_history / commitment_snapshot table), the SAME asOfForecastDate
// mechanism (or a sibling asOfDate param following this pattern) can be
// extended to cover them, evolving this into a full historical project
// snapshot — without another architectural refactor and without introducing
// a parallel engine. Until then, callers requesting a snapshot should treat
// only the EAC-derived figures as "as of" that period.
// ─── extractFetchedData() ──────────────────────────────────────────────────
// Phase 4 refactor: the project-invariant portion of buildCumulativeData()'s
// original body, extracted verbatim (no logic changes) so it can run ONCE
// and be reused across multiple forecastDate points by
// buildCumulativeSeries(), instead of being re-run per point. Every one of
// these queries was confirmed independent of asOfForecastDate before this
// extraction (see the Phase 4 Gate Review this refactor was validated
// against). computeSnapshotFromFetchedData() below is the ONLY consumer of
// this function's output — no other caller should read from it directly.
async function extractFetchedData(projectId) {
  const id = projectId


  // 1. Cost Codes (parents + children)
  const { data: allCodes } = await supabase
    .from('cost_codes')
    .select('id, code, description, parent_id, level, cost_type, resource_dict_code, indirect_category_id')
    .eq('project_id', id)
    .eq('is_active', true)
    .order('code')

  // 1a. Indirect Categories — user-defined groupings of Indirect Cost Codes.
  // If none exist, Indirect stays as a single tab (backward compatible).
  const { data: indirectCategoriesData } = await supabase
    .from('indirect_categories')
    .select('id, name, sort_order')
    .eq('project_id', id)
    .order('sort_order', { ascending: true, nullsFirst: false })
    .order('name')

  // 1c. Project-level metrics — Contract Value, Cash In, Cash Out. These are
  // project-wide figures, not per-Cost-Code, so they only appear on
  // grandTotals/meta, never distributed across directRows/indirectRows.
  // NOTE: Cash Out here (from cf_payment_entries) is a DIFFERENT metric from
  // grandTotals.cashOut elsewhere in this file (that one reads from
  // finance_transactions, currently disabled/returns 0 due to a documented
  // schema mismatch). To avoid confusion between the two, this project-level
  // figure is exposed under distinct names: cashInTotal, cashOutTotal, netCash.
  const [projectRes, cashInRes, cashOutRes] = await Promise.all([
    supabase.from('projects').select('contract_value').eq('id', id).single(),
    supabase.from('cash_in_entries').select('collected_amount').eq('project_id', id),
    supabase.from('cf_payment_entries').select('amount_inc_vat').eq('project_id', id),
  ])
  const contractValue = parseFloat(projectRes.data?.contract_value) || 0
  const cashInTotal = (cashInRes.data || []).reduce((s, r) => s + (parseFloat(r.collected_amount) || 0), 0)
  const cashOutTotal = (cashOutRes.data || []).reduce((s, r) => s + (parseFloat(r.amount_inc_vat) || 0), 0)
  const netCash = cashInTotal - cashOutTotal

  // 1b. Resource Dictionary — to know each Resource Code's type (material/labour/subcontract/equipment)
  const { data: resourceDictData } = await supabase
    .from('resource_dictionary')
    .select('code, resource_type')
    .eq('project_id', id)
  const resourceTypeMap = {}
  ;(resourceDictData || []).forEach(d => { resourceTypeMap[d.code] = d.resource_type })

  // 2. BAC — Direct budget comes from tender_direct_items (Resource Code level).
  // Indirect budget comes from tender_indirect_items (Cost Code level, single-level — no Resource Code for Indirect).
  const [tenderRes, tenderIndirectRes] = await Promise.all([
    supabase.from('tender_direct_items')
      .select('cost_code, resource_code, resource_description, row_type, material_total, labour_total, subcontractor_total, equipment_total, package_name, sort_order')
      .eq('project_id', id)
      .in('row_type', ['material','labour','subcontract','equipment'])
      .order('sort_order', { ascending: true }),
    supabase.from('tender_indirect_items')
      .select('cost_code, tender_budget, is_package_header')
      .eq('project_id', id)
      .eq('is_package_header', false),
  ])

  // Indirect budget map: normalized cost_code (text) -> summed tender_budget
  const indirectBudgetMap = {}
  for (const r of (tenderIndirectRes.data || [])) {
    if (!r.cost_code) continue
    const key = String(r.cost_code).trim().toLowerCase()
    indirectBudgetMap[key] = (indirectBudgetMap[key] || 0) + (parseFloat(r.tender_budget) || 0)
  }

  // 3. Commitment — via DB function
  const { data: commitmentData } = await supabase
    .rpc('get_commitment_by_subcode', { p_project_id: id })

  // 4. Actual Cost — All approved sources
  // Step 1: get approved IPC ids for this project
  const { data: approvedIpcs } = await supabase
    .from('ipc_certificates')
    .select('id, approved_date')
    .eq('project_id', id)
    .eq('status', 'approved')

  const approvedIpcIds = (approvedIpcs || []).map(i => i.id)
  const approvedIpcDateMap = {}
  for (const i of (approvedIpcs || [])) approvedIpcDateMap[i.id] = i.approved_date

  // Step 2: get line items for those IPCs
  const ipcLinesData = []
  if (approvedIpcIds.length) {
    const { data } = await supabase
      .from('ipc_line_items')
      .select('sub_code, this_period_amount, invoiced_amount, ipc_id')
      .in('ipc_id', approvedIpcIds)
      .limit(5000)
    ipcLinesData.push(...(data || []))
  }

  const [storeRes, payrollRes, costCodesRes] = await Promise.all([
    supabase.rpc('get_actual_cost_by_subcode', { p_project_id: id }),
    supabase.from('payroll_entries').select('sub_code_id, total_salary, year, month').eq('project_id', id).eq('status', 'approved'),
    supabase.from('cost_codes').select('id, code').eq('project_id', id).limit(5000),
  ])

  // 5. Accrual
  // Step: get pending IPC ids
  const { data: pendingIpcs } = await supabase
    .from('ipc_certificates')
    .select('id')
    .eq('project_id', id)
    .in('status', ['submitted', 'under_review'])

  const pendingIpcIds = (pendingIpcs || []).map(i => i.id)
  const pendingLinesData = []
  if (pendingIpcIds.length) {
    const { data } = await supabase
      .from('ipc_line_items')
      .select('sub_code, this_period_amount')
      .in('ipc_id', pendingIpcIds)
    pendingLinesData.push(...(data || []))
  }

  const [accrualManRes, payrollDraftRes] = await Promise.all([
    supabase.from('accrual_entries').select('sub_code_id, amount, month, year').eq('project_id', id),
    supabase.from('payroll_entries').select('sub_code_id, total_salary').eq('project_id', id).eq('status', 'draft'),
  ])

  // 6. Cash Out
  // Fix: finance_transactions' schema no longer matches this legacy
  // query (documented and worked around in Finance.jsx's fetchCashOut()
  // — the table's columns changed and the old cost_code_id/
  // transaction_type/date/status shape no longer exists on it).
  // Returning an empty result directly avoids the 400 error.
  const cashOutData = []

  // 7. EAC — Fix: previously read from forecast_eac, a table nobody
  // forecast_active_date is NO LONGER read here. Phase 2 replaced the old
  // "one globally active forecast period" concept with an explicit
  // Draft/Revision model — the Engine must never infer forecast context
  // from global mutable state. asOfForecastDate is now the ONLY way a
  // caller can request a specific forecast_date's EAC/ETC values; when it
  // is not supplied, this function returns the plain live financial state
  // (every code resolved via the Not-Forecasted Budget fallback, exactly
  // as if no forecast existed at all) — deterministic, with zero hidden
  // dependency on any "currently active" setting.
  const [forecastHistoryRes, directEvRes, indirectEvRes] = await Promise.all([
    supabase.from('forecast_history').select('cost_code, sub_code, row_key, eac_amount, etc_amount, source, forecast_date').eq('project_id', id),
    // Earned Value — Direct: direct_budget_ev keyed by resource_code (Resource
    // Code level). Indirect: indirect_budget_ev keyed by cost_code (Cost Code
    // level directly, since Indirect has no Resource Code level). ev_amount is
    // pre-computed and persisted at save time by Budget.jsx / IndirectBudget.jsx
    // — read here as-is, never recalculated. This is the single place EV is
    // read from for every consumer (Cost Control, and later Executive Report,
    // Dashboard, Analytics).
    supabase.from('direct_budget_ev').select('resource_code,ev_amount').eq('project_id', id),
    supabase.from('indirect_budget_ev').select('cost_code,ev_amount').eq('project_id', id),
  ])

  // Petty Cash — this query was interleaved deep inside the original
  // function's calculation body (between Payroll actuals and the
  // Previous-Actual derivation), not grouped with the other queries at the
  // top — moved here verbatim as part of this refactor's consolidation of
  // ALL Supabase calls into extractFetchedData(), per the approved design
  // ("every Supabase call, wherever it originally sat, becomes part of the
  // single fetch"). The query itself (table, columns, filter) is unchanged.
  const { data: pcData } = await supabase
    .from('petty_cash_vouchers')
    .select('sub_code,amount_exc_vat')
    .eq('project_id', id)

  return {
    allCodes, indirectCategoriesData, contractValue, cashInTotal, cashOutTotal, netCash,
    resourceTypeMap, tenderRes, tenderIndirectRes, indirectBudgetMap, commitmentData,
    approvedIpcs, approvedIpcDateMap, ipcLinesData, storeRes, payrollRes, costCodesRes,
    pendingLinesData, accrualManRes, payrollDraftRes, cashOutData, pcData,
    forecastHistoryRes, directEvRes, indirectEvRes,
  }
}

export async function buildCumulativeData(projectId, selectedMonth, selectedYear, asOfForecastDate = null) {
  if (!projectId) return { directRows: [], indirectRows: [], directTotals: {}, indirectTotals: {}, grandTotals: {}, meta: {} }


  // Default to the current calendar month/year when the caller doesn't
  // specify one (e.g. Executive Report only needs cumulative totals per
  // Cost Code, not a specific "this period" split, so it calls this
  // function without these two arguments). Without this default,
  // selectedMonth/selectedYear stayed undefined, producing a
  // month=NaN&year=undefined query — a 400 Bad Request that silently
  // left several fields at 0 for every row.
  const now = new Date()
  selectedMonth = selectedMonth ?? (now.getMonth() + 1)
  selectedYear = selectedYear ?? now.getFullYear()

  const id = projectId

  const fetchedData = await extractFetchedData(id)
  return computeSnapshotFromFetchedData(fetchedData, selectedMonth, selectedYear, asOfForecastDate)
}

// ─── computeSnapshotFromFetchedData() ──────────────────────────────────────
// Phase 4 refactor: the asOfForecastDate-dependent calculation portion of
// buildCumulativeData()'s original body, extracted verbatim (no financial
// logic changed) so both buildCumulativeData() (single point) and
// buildCumulativeSeries() (multiple points) call this SAME code path —
// never two separate copies of the calculation. Every line below this
// point is an unmodified copy of the original function body.
function computeSnapshotFromFetchedData(fetchedData, selectedMonth, selectedYear, asOfForecastDate) {
  const {
    allCodes, indirectCategoriesData, contractValue, cashInTotal, cashOutTotal, netCash,
    resourceTypeMap, tenderRes, tenderIndirectRes, indirectBudgetMap, commitmentData,
    approvedIpcs, approvedIpcDateMap, ipcLinesData, storeRes, payrollRes, costCodesRes,
    pendingLinesData, accrualManRes, payrollDraftRes, cashOutData, pcData,
    forecastHistoryRes, directEvRes, indirectEvRes,
  } = fetchedData


  // eacData is populated ONLY when the caller explicitly supplied
  // asOfForecastDate — there is no implicit fallback to any "active" date
  // anymore. Without asOfForecastDate, eacData is empty by design, and
  // every code deterministically resolves through the Not-Forecasted
  // Budget fallback below (see resolveForecastOwnership()).
  const eacData = asOfForecastDate
    ? (forecastHistoryRes.data || [])
        .filter(r => r.forecast_date === asOfForecastDate)
        .map(r => ({
          budget_row_id: null, // forecast_history has no budget_row_id; dedup not needed since it's already one row per (source, row_key, forecast_date)
          sub_code: r.row_key || r.sub_code || r.cost_code,
          cost_code_id: null,
          eac_amount: r.eac_amount,
          // Carried through for the generic ownership resolver's Indirect rule
          // (Forecast = ETC; EAC = Actual + ETC, verified/derived at the Engine
          // itself, not just trusted from whatever was last written by the UI).
          etc_amount: r.etc_amount,
          source: r.source,
        }))
    : []

  // Resource-level Forecast Override detection — matches DirectForecast.jsx's
  // own resourceEac() rule exactly: a Resource Code is "overridden" when a
  // forecast_history row EXISTS for the active period with row_key equal to
  // that Resource Code (rows[res.code] != null in DirectForecast.jsx), NOT
  // when its resulting eac_amount happens to be non-zero. Using "!= 0" as the
  // override signal would wrongly treat a deliberately-entered EAC of 0 as
  // "no override" and fall back to summing Sub Codes — existence in
  // forecast_history, independent of amount, is the only correct signal.
  // Built from the SAME eacData rows every other EAC figure already comes
  // from — no new query, no second source.
  const overriddenRowKeys = new Set(eacData.map(r => (r.sub_code || '').toLowerCase()))

  // Per-row_key raw forecast entry (eac_amount/etc_amount/source), for the
  // generic ownership resolver below. This is what lets the Engine itself
  // apply "Direct: EAC = Forecast" vs "Indirect: EAC = Actual + ETC" per
  // node, instead of only trusting whatever eac_amount happens to already
  // be stored — the Indirect rule is now verified/derived here, at read
  // time, not merely assumed from what the UI wrote at save time.
  const forecastEntryByRowKey = {}
  for (const r of eacData) {
    const key = (r.sub_code || '').toLowerCase()
    if (!key) continue
    forecastEntryByRowKey[key] = { eac_amount: r.eac_amount, etc_amount: r.etc_amount, source: r.source }
  }

  // ─── Generic Forecast Ownership Resolver ────────────────────────────────
  // Implements the required specification exactly, as a single recursive
  // function usable at ANY hierarchy depth (Sub Code, Resource Code, Cost
  // Code, and any future level) — replacing the previous hardcoded,
  // per-level override checks with one general rule:
  //
  //   1. Ownership is resolved BEFORE any aggregation.
  //   2. If this node has an explicit forecast (a forecast_history row
  //      exists for its row_key, in the active period) → it IS the Forecast
  //      Owner. Its EAC is derived from that forecast (Direct: EAC =
  //      Forecast directly; Indirect: EAC = Actual + ETC). Aggregation from
  //      children STOPS here — children are resolved recursively purely for
  //      their own display, but their resolved eac values are NOT summed
  //      into this node's eac.
  //   3. If this node has NO explicit forecast:
  //        - If it has children, resolve each child recursively (same
  //          function, same rules) and this node's eac = SUM of the
  //          children's already-resolved eac values. Nothing here
  //          re-derives or re-applies Budget at this level — only sums.
  //        - If it has NO children (a true leaf with no forecast at all),
  //          EAC = this node's own Budget. Never Actual.
  //
  // "resolvedEac" on the returned object is the single number every
  // consumer should use — it already accounts for ownership, so a parent
  // simply summing its children's resolvedEac always produces the correct
  // total, with no risk of a parent mixing Budget-fallback logic with
  // children that used Actual (there is no Actual fallback anywhere in this
  // function — Budget is the only fallback, exactly per spec).
  //
  // node shape: { rowKey, budget, actual, children: [node, ...] }
  // Returns: { rowKey, eac, etc, isOwner, ownSource }
  function resolveForecastOwnership(node) {
    const key = (node.rowKey || '').toLowerCase()
    const entry = key ? forecastEntryByRowKey[key] : null

    if (entry != null) {
      // Rule: this node HAS an explicit forecast → it is the Forecast
      // Owner. Aggregation from children stops — resolved here directly.
      let eac
      if (entry.source === 'indirect') {
        // Indirect: Forecast = ETC. EAC = Actual + ETC, derived/verified
        // here rather than trusting a possibly-stale stored eac_amount.
        const etcVal = parseFloat(entry.etc_amount)
        eac = !isNaN(etcVal) ? (node.actual || 0) + etcVal : (parseFloat(entry.eac_amount) || 0)
      } else {
        // Direct: Forecast = EAC directly.
        eac = parseFloat(entry.eac_amount) || 0
      }
      const etc = eac - (node.actual || 0)
      // Children are still resolved (for their own display purposes only —
      // "children become display-only"), but their resolvedEac is never
      // summed into this owner node's eac.
      const resolvedChildren = (node.children || []).map(resolveForecastOwnership)
      return { rowKey: node.rowKey, eac, etc, isOwner: true, resolvedChildren }
    }

    if ((node.children || []).length > 0) {
      // Rule: no forecast here, but has children → recurse, then aggregate
      // ONLY the already-resolved child values. No Budget fallback applied
      // at this level — that would violate "Budget fallback must never be
      // applied after aggregation."
      const resolvedChildren = node.children.map(resolveForecastOwnership)
      const eac = resolvedChildren.reduce((s, c) => s + (c.eac || 0), 0)
      const etc = eac - (node.actual || 0)
      return { rowKey: node.rowKey, eac, etc, isOwner: false, resolvedChildren }
    }

    // Rule: true leaf, no forecast at all → EAC = Budget, never Actual.
    const eac = node.budget || 0
    const etc = eac - (node.actual || 0)
    return { rowKey: node.rowKey, eac, etc, isOwner: false, resolvedChildren: [] }
  }

  // NOTE: previously this section also queried the legacy `eac_entries` table
  // for "previous month EAC" as a second, parallel data source. That table is
  // no longer written to and is removed here — forecast_history (via
  // asOfForecastDate) is now the ONLY source for historical/previous EAC.
  // Trend Indicators / Executive Report should get "previous EAC" by calling
  // this same function twice with two different asOfForecastDate values,
  // never by reading eac_entries directly.

  // ===================== AGGREGATE BY COST CODE =====================
  const agg = {}

  function ensure(codeId) {
    if (!agg[codeId]) agg[codeId] = {
      bac: 0, commitment: 0,
      actualTotal: 0, actualThisPeriod: 0, actualPrevious: 0,
      accrual: 0, totalActualCost: 0,
      cashOut: 0, cashOutThisPeriod: 0,
      eac: 0, prevEac: 0,
      varianceEac: 0, varianceCompletion: 0,
      invMaterials: 0, invSubcontracts: 0, invEquipment: 0,
      invPettyCash: 0, invOthers: 0, invSalaries: 0,
      earnedValue: 0, cpi: 0,
    }
  }

  // Commitment — from DB function result
  for (const r of (commitmentData || [])) {
    if (!r.cost_code_id) continue
    ensure(r.cost_code_id)
    agg[r.cost_code_id].commitment += parseFloat(r.total) || 0
  }

  // Actual — IPC
  // Build sub_code → cost_code_id map (case-insensitive), and its reverse
  // (cost_code_id → code) for the deliberate-zero-EAC check below.
  const subCodeMap = {}
  const codeById = {}
  for (const cc of (costCodesRes.data || [])) {
    subCodeMap[cc.code.toLowerCase()] = cc.id
    codeById[cc.id] = cc.code
  }

  // BAC — apply package budgets now that subCodeMap is ready
  // Also build subBudgetMap: parentCcId → Map<childCode, budget>
  // so buildParentRow can distribute BAC to child cost_codes by matching child.code to sub_code
  const subBudgetMap = {}  // parentCcId → { [childCode.toLowerCase()]: budget }
  const ccPackageNames = {} // parentCcId → [package_name, ...]

  // Direct BAC — from tender_direct_items, keyed by (cost_code_id, resource_code).
  // This feeds Resource Code (level 4) rows directly; Sub Codes underneath carry
  // Commitment/Actual only, no budget of their own (matching the agreed structure).
  const resourceBudgetMap = {} // parentCcId → { [resource_code.toLowerCase()]: budget }
  for (const r of (tenderRes.data || [])) {
    const ccId = subCodeMap[(r.cost_code || '').trim().toLowerCase()]
    if (!ccId) continue
    const rcode = (r.resource_code || '').trim().toLowerCase()
    if (!rcode) continue // rows without a Resource Code can't be matched to a cost_codes.resource_dict_code row
    const resTotal = r.row_type === 'material' ? r.material_total
      : r.row_type === 'labour' ? r.labour_total
      : r.row_type === 'subcontract' ? r.subcontractor_total
      : r.equipment_total
    const b = parseFloat(resTotal) || 0
    if (b <= 0) continue
    if (!resourceBudgetMap[ccId]) resourceBudgetMap[ccId] = {}
    resourceBudgetMap[ccId][rcode] = (resourceBudgetMap[ccId][rcode] || 0) + b
    if (!ccPackageNames[ccId]) ccPackageNames[ccId] = []
    if (r.package_name && !ccPackageNames[ccId].includes(r.package_name)) ccPackageNames[ccId].push(r.package_name)
  }

  // Indirect BAC — apply budget from tender_indirect_items, matched to Cost Codes by name (text)
  for (const [ccCodeKey, budget] of Object.entries(indirectBudgetMap)) {
    const ccId = subCodeMap[ccCodeKey]
    if (!ccId || budget <= 0) continue
    ensure(ccId)
    agg[ccId].bac += budget
  }

  // Actual — IPC line items by sub_code
  for (const r of ipcLinesData) {
    if (!r.sub_code) continue
    const ccId = subCodeMap[r.sub_code.toLowerCase()]
    if (!ccId) continue
    ensure(ccId)
    const amt = parseFloat(r.invoiced_amount ?? r.this_period_amount) || 0
    agg[ccId].actualTotal += amt
    agg[ccId].invSubcontracts += amt
    const approvedDate = approvedIpcDateMap[r.ipc_id]
    if (approvedDate) {
      const d = new Date(approvedDate)
      if (d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear)
        agg[ccId].actualThisPeriod += amt
    }
  }

  // Actual — Store Issues
  for (const r of (storeRes.data || [])) {
    if (!r.cost_code_id) continue
    ensure(r.cost_code_id)
    const amt = parseFloat(r.total) || 0
    agg[r.cost_code_id].actualTotal += amt
    agg[r.cost_code_id].totalActualCost += amt
    agg[r.cost_code_id].invMaterials += amt
  }

  // Actual — Payroll
  for (const r of (payrollRes.data || [])) {
    if (!r.sub_code_id) continue
    ensure(r.sub_code_id)
    const amt = parseFloat(r.total_salary) || 0
    agg[r.sub_code_id].actualTotal += amt
    agg[r.sub_code_id].invSalaries += amt
    if (r.month === selectedMonth && r.year === selectedYear) agg[r.sub_code_id].actualThisPeriod += amt
  }

  // Actual — Petty Cash (by sub_code via subCodeMap). pcData now comes from
  // extractFetchedData() (see the note there) — same query, same data,
  // read here instead of fetched here.
  for (const r of (pcData || [])) {
    if (!r.sub_code) continue
    const ccId = subCodeMap[(r.sub_code||'').toLowerCase()]
    if (!ccId) continue
    ensure(ccId)
    const amt = parseFloat(r.amount_exc_vat) || 0
    agg[ccId].actualTotal      += amt
    agg[ccId].totalActualCost  += amt
    agg[ccId].invPettyCash     = (agg[ccId].invPettyCash || 0) + amt
  }

  // Previous Actual = Total - This Period
  for (const cid of Object.keys(agg)) {
    agg[cid].actualPrevious = agg[cid].actualTotal - agg[cid].actualThisPeriod
  }

  // Accrual — Manual
  for (const r of (accrualManRes.data || [])) {
    if (!r.sub_code_id) continue
    ensure(r.sub_code_id)
    agg[r.sub_code_id].accrual += parseFloat(r.amount) || 0
  }
  // Accrual — IPC Pending (by sub_code)
  for (const r of pendingLinesData) {
    if (!r.sub_code) continue
    const ccId = subCodeMap[r.sub_code.toLowerCase()]
    if (!ccId) continue
    ensure(ccId)
    agg[ccId].accrual += parseFloat(r.this_period_amount) || 0
  }
  // Accrual — Payroll Draft
  for (const r of (payrollDraftRes.data || [])) {
    if (!r.sub_code_id) continue
    ensure(r.sub_code_id)
    agg[r.sub_code_id].accrual += parseFloat(r.total_salary) || 0
  }
  // Total Actual Cost = Invoiced (actual) + Accrual
  for (const cid of Object.keys(agg)) {
    agg[cid].totalActualCost = agg[cid].actualTotal + agg[cid].accrual
  }

  // Cash Out
  for (const r of (cashOutData || [])) {
    if (!r.cost_code_id) continue
    ensure(r.cost_code_id)
    const amt = parseFloat(r.amount_inc_vat) || 0
    agg[r.cost_code_id].cashOut += amt
    if (r.date) {
      const d = new Date(r.date)
      if (d.getMonth() + 1 === selectedMonth && d.getFullYear() === selectedYear) agg[r.cost_code_id].cashOutThisPeriod += amt
    }
  }

  // EAC — use subCodeMap (code→id, all levels) to map sub_code to child ccId
  // Each budget_row_id is unique — deduplicate then sum per child ccId
  // NOTE: previously skipped any row where amt === 0 ("if (amt === 0) continue").
  // That silently dropped deliberately-entered EAC/ETC values of exactly 0
  // (e.g. Indirect ETC left empty → treated as 0 → EAC = Actual, which can
  // itself be 0 for a code with no cost yet) — a real, intentional value is
  // not the same as "no forecast entered", and dropping it also meant such
  // rows never appeared in overriddenRowKeys, silently breaking override
  // detection for exactly-zero overrides. Existence in forecast_history is
  // the only correct signal (see overriddenRowKeys above); zero is a valid amount.
  const seenBudgetRowIds = new Set()
  for (const r of (eacData || [])) {
    const amt = parseFloat(r.eac_amount) || 0
    if (r.budget_row_id) {
      if (seenBudgetRowIds.has(r.budget_row_id)) continue
      seenBudgetRowIds.add(r.budget_row_id)
    }
    if (!r.sub_code) continue
    const ccId = subCodeMap[r.sub_code.toLowerCase()]
    if (!ccId) continue
    ensure(ccId)
    agg[ccId].eac += amt
  }

  // Prev EAC: no longer populated internally (legacy eac_entries removed —
  // see note above forecastHistoryRes usage). prevEac/varianceEac stay 0
  // here; a caller wanting "previous EAC" should call buildCumulativeData()
  // a second time with an earlier asOfForecastDate and diff the two
  // grandTotals.eac (or per-row eac) values itself. Kept as a field on agg
  // for structural/back-compat reasons only.

  // Parent ccIds — EAC will be summed from children in buildParentRow, leave as 0
  // Resource Codes (level 4) are an intermediate grouping level too — they don't carry
  // their own actuals/EAC directly (those live on the Sub Codes beneath them)
  const parentCcIds = new Set((allCodes || []).filter(c => c.level === 1 || c.level === 4).map(c => c.id))

  // Child codes with no forecast_history record at all → resolved via the
  // SAME resolveForecastOwnership() function used at every other level
  // (Resource, Cost Code) — called here with children:[] since at this
  // point in the pipeline each code is being resolved in isolation, as a
  // leaf. This guarantees the leaf rule (owner forecast → its EAC; no
  // forecast → Budget, never Actual) is applied identically everywhere,
  // not as a separately-written check that could drift from the Resource/
  // Cost-Code-level logic below.
  for (const cid of Object.keys(agg)) {
    if (parentCcIds.has(cid)) continue // parents resolved later in buildParentRow, from already-resolved children
    const code = (codeById[cid] || '').toLowerCase()
    const leafOwnership = resolveForecastOwnership({ rowKey: code, budget: agg[cid].bac || 0, actual: agg[cid].totalActualCost || 0, children: [] })
    agg[cid].eac = leafOwnership.eac
    agg[cid].varianceEac = agg[cid].eac - agg[cid].prevEac
    agg[cid].varianceCompletion = agg[cid].bac - agg[cid].eac
  }

  // Earned Value — Direct: attaches to the Resource Code (level 4) itself,
  // matched via subCodeMap by resource_code (same case-insensitive matching
  // pattern used throughout this file). This is where EV genuinely lives for
  // Direct Works — Sub Codes underneath don't carry their own EV.
  for (const r of (directEvRes.data || [])) {
    if (!r.resource_code) continue
    const rcId = subCodeMap[r.resource_code.toLowerCase()]
    if (!rcId) continue
    ensure(rcId)
    agg[rcId].earnedValue += parseFloat(r.ev_amount) || 0
  }
  // Earned Value — Indirect: attaches directly to the Cost Code (level 1),
  // matched via subCodeMap by cost_code. Indirect has no Resource Code level,
  // so EV lives on the Cost Code itself here (not a rollup).
  for (const r of (indirectEvRes.data || [])) {
    if (!r.cost_code) continue
    const ccId = subCodeMap[r.cost_code.toLowerCase()]
    if (!ccId) continue
    ensure(ccId)
    agg[ccId].earnedValue += parseFloat(r.ev_amount) || 0
  }
  // CPI = Earned Value / Total Actual Cost, computed once per code at the
  // level EV actually lives (Resource Code for Direct, Cost Code for Indirect).
  // Parent (Cost Code) rollups for Direct are computed separately in
  // buildParentRow below, from the Resource Code children — never
  // recalculated independently.
  for (const cid of Object.keys(agg)) {
    agg[cid].cpi = (agg[cid].earnedValue && agg[cid].totalActualCost) ? (agg[cid].earnedValue / agg[cid].totalActualCost) : 0
  }

  // ===================== BUILD TREE =====================
  const parents = (allCodes || []).filter(c => c.level === 1)
  // Resource Codes (level 4) sit between Cost Code and Sub Code. A Sub Code's parent_id
  // may point directly at the Cost Code (legacy / not yet linked) OR at a Resource Code
  // that itself belongs to the Cost Code — both must roll up into the same Cost Code row.
  const resourceCodes = (allCodes || []).filter(c => c.level === 4)
  const children = (allCodes || []).filter(c => c.level === 2)

  const directParents = parents.filter(p => p.cost_type === 'direct')
  const indirectParents = parents.filter(p => p.cost_type === 'indirect')

  function buildParentRow(parent) {
    const resourcesUnderParent = resourceCodes.filter(r => r.parent_id === parent.id)
    const resourceIds = new Set(resourcesUnderParent.map(r => r.id))
    // Sub Codes directly under the Cost Code (legacy, not yet linked to a Resource Code)
    // PLUS Sub Codes nested one level deeper under a Resource Code that belongs to this Cost Code
    const subs = children.filter(c => c.parent_id === parent.id || resourceIds.has(c.parent_id))
    const parentAgg = { bac:0, commitment:0, actualTotal:0, actualThisPeriod:0, actualPrevious:0, accrual:0, totalActualCost:0, cashOut:0, cashOutThisPeriod:0, eac:0, prevEac:0, varianceEac:0, varianceCompletion:0, invMaterials:0, invSubcontracts:0, invEquipment:0, invPettyCash:0, invOthers:0, invSalaries:0, earnedValue:0, cpi:0 }

    // Include amounts posted directly on the parent cost code (except eac/cpi —
    // eac handled explicitly below via the override check, cpi because it's
    // always derived, never summed)
    const parentDirect = agg[parent.id] || {}
    for (const k of Object.keys(parentAgg)) {
      if (k === 'eac' || k === 'prevEac' || k === 'cpi') continue
      parentAgg[k] += (parentDirect[k] || 0)
    }

    // Cost-Code-level Forecast Ownership — resolved via the generic
    // resolveForecastOwnership() function (see above), not a hardcoded
    // per-level check. If this Cost Code itself owns an explicit forecast,
    // ownership.isOwner is true and ownership.eac is the final value;
    // aggregation from Sub Codes must not add to it (enforced below).
    const parentCode = (parent.code || '').trim().toLowerCase()
    const parentOwnership = resolveForecastOwnership({ rowKey: parentCode, budget: parentDirect.bac || 0, actual: parentDirect.totalActualCost || 0, children: [] })
    const parentIsOverridden = parentOwnership.isOwner
    parentAgg.eac = parentIsOverridden ? parentOwnership.eac : 0 // else summed from subRows below
    parentAgg.prevEac = parentDirect.prevEac || 0

    // Resource Code budget map for this parent — comes from tender_direct_items directly
    const resBudgets = resourceBudgetMap[parent.id] || {}

    const subRows = subs.map(sub => {
      const d = agg[sub.id] || {}
      // Sub Codes no longer carry budget of their own — Commitment/Actual/EAC only.
      // (Legacy subBudgetMap from indirect packages still applies for Indirect Works.)
      let childBac = 0
      const legacySubBudgets = subBudgetMap[parent.id] || {}
      if (Object.keys(legacySubBudgets).length > 0) {
        childBac = legacySubBudgets[(sub.code||'').trim().toLowerCase()] || (d.bac || 0)
      }
      // Explicit 0 fallback for earnedValue/cpi — Sub Codes never carry their own
      // Earned Value (see comment below), but if this sub code has no entry in
      // agg at all (no commitment/actual/accrual from any source), d.earnedValue
      // would otherwise be undefined rather than 0. Guaranteeing a real number
      // here matters for any consumer beyond RowCells (e.g. Executive Report)
      // that may not apply the same truthy-check safety before formatting.
      // Bug fix: varianceCompletion/varianceEac were previously carried over
      // from `d` (agg[sub.id]), which computed them earlier using bac=0 for
      // Direct Sub Codes (Direct Sub Codes have no budget of their own —
      // only Resource Codes do, via resourceBudgetMap, resolved later than
      // that earlier pass). That produced a wrong Variance figure at the
      // Sub Code display level even though EAC itself was always correct.
      // Recomputed here, now that childBac reflects the real value for this
      // Sub Code (its own legacy budget share if any, else 0 — a Direct Sub
      // Code's meaningful Variance lives at the Resource level, where real
      // budget exists; this Sub-Code-level figure is 0-budget by design and
      // now correctly reflects that instead of an earlier miscomputed value).
      const enriched = {
        ...d, bac: childBac, earnedValue: d.earnedValue || 0, cpi: d.cpi || 0,
        varianceEac: (d.eac || 0) - (d.prevEac || 0),
        varianceCompletion: childBac - (d.eac || 0),
      }
      // Accumulate all fields from children to parent (including eac), but NOT
      // bac (comes from resourceBudgetMap, added separately below) or cpi
      // (always derived from rolled-up earnedValue/totalActualCost, never summed) —
      // Sub Codes don't carry their own Earned Value, so earnedValue here will
      // always be 0 from this loop; Direct EV enters via resourceRows below,
      // Indirect EV enters via parentDirect above.
      for (const k of Object.keys(parentAgg)) {
        if (k === 'varianceEac' || k === 'varianceCompletion' || k === 'bac' || k === 'cpi') continue
        // Skip eac here when the parent Cost Code itself is overridden — its
        // final eac was already set directly from parentDirect.eac above,
        // and must not also be re-summed from Sub Codes underneath (that
        // rollup is exactly what the override is meant to stop, per the
        // same business rule as the Direct Resource override).
        if (k === 'eac' && parentIsOverridden) continue
        parentAgg[k] += (enriched[k] || 0)
      }
      return { ...sub, ...enriched, isChild: true }
    })

    // Resource Code rollup — budget now comes directly from tender_direct_items
    // (resourceBudgetMap), not from summing Sub Codes underneath. Commitment/Actual/EAC
    // still roll up from the Sub Codes, since that's where real transactions (PO, IPC...) live.
    // Earned Value is the exception: it belongs to the Resource Code itself
    // (direct_budget_ev, keyed by resource_code) — not a rollup from its Sub
    // Codes, which don't carry their own EV.
    const resourceRows = resourcesUnderParent.map(res => {
      const subsOfThisResource = subRows.filter(s => s.parent_id === res.id)
      const dictCode = (res.resource_dict_code || res.code || '').trim().toLowerCase()
      const resourceBac = resBudgets[dictCode] || 0
      const rtype = resourceTypeMap[res.resource_dict_code || res.code] || null
      const resAgg = subsOfThisResource.reduce((acc, r) => {
        for (const k of ['commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac']) {
          acc[k] = (acc[k] || 0) + (r[k] || 0)
        }
        return acc
      }, {})
      // Resource Code Forecast Ownership — resolved via the generic
      // resolveForecastOwnership() function, exactly per spec:
      //   1. Does the Resource itself own an explicit forecast? → owner,
      //      aggregation from Sub Codes stops.
      //   2. Otherwise, does it have Sub Codes? → recurse into each Sub
      //      Code (itself resolved via the same function), sum only their
      //      already-resolved eac values.
      //   3. Otherwise (no forecast, no Sub Codes) → EAC = this Resource's
      //      own Budget. Never Actual.
      const resCode = (res.code || '').trim().toLowerCase()
      const dictCodeKey = dictCode
      const resourceOwnershipNode = {
        rowKey: overriddenRowKeys.has(resCode) ? resCode : (overriddenRowKeys.has(dictCodeKey) ? dictCodeKey : resCode),
        budget: resourceBac,
        actual: resAgg.totalActualCost || 0,
        children: subsOfThisResource.map(s => ({
          rowKey: (s.code || '').toLowerCase(),
          budget: s.bac || 0, // Direct Sub Codes carry no budget of their own (always 0) — correct per spec, since Budget lives on the Resource for Direct
          actual: s.totalActualCost || 0,
          children: [],
        })),
      }
      const resourceOwnership = resolveForecastOwnership(resourceOwnershipNode)
      const isOverridden = resourceOwnership.isOwner
      const resourceEac = resourceOwnership.eac
      // parentAgg.eac was already summed purely from Sub Codes above (subRows
      // loop) using each Sub Code's own resolved eac (resAgg.eac reflects
      // that same sum for just this Resource's subs). That sum is correct
      // ONLY when resourceEac ends up equal to it — i.e. the "has any sub
      // forecast" branch, where the Resource's final value truly is the
      // sum of its Sub Codes. In BOTH other cases — an explicit override,
      // or the new Budget-fallback (nothing forecasted under this Resource
      // at all) — resourceEac is a different, correctly-resolved number
      // that doesn't come from summing Sub Codes, so parentAgg.eac's
      // earlier sub-based contribution for this Resource must be corrected
      // to match. Comparing resourceEac to resAgg.eac directly (rather than
      // branching on isOverridden alone) automatically covers both cases
      // with one correction, and is a no-op whenever they already agree.
      // Skipped entirely when the Cost Code itself is overridden (Indirect-
      // style override on the parent) — in that case parentAgg.eac is
      // already fixed at the parent's own value and Sub Code eac was never
      // added in the first place, so this correction would double-count.
      if (!parentIsOverridden && resourceEac !== (resAgg.eac || 0)) parentAgg.eac += (resourceEac - (resAgg.eac || 0))
      const resourceEv = agg[res.id]?.earnedValue || 0
      const resourceCpi = (resourceEv && resAgg.totalActualCost) ? (resourceEv / resAgg.totalActualCost) : 0
      // Bug fix (same class as the Sub Code fix above): Resource Codes never
      // had varianceCompletion/varianceEac computed at all — this is the
      // true budget-bearing leaf for Direct Forecast, so this is where that
      // metric is actually meaningful, resolved once here from the final
      // resourceEac/resourceBac (never recomputed by any consumer downstream).
      const resourceVarianceCompletion = resourceBac - resourceEac
      const resourceVarianceEac = resourceEac - (resAgg.prevEac || 0)
      // NOTE: `children` here is the actual computed Sub Code rows
      // (subsOfThisResource) for this Resource — previously omitted from
      // this return value entirely (only used internally, above, to feed
      // resourceOwnershipNode for this function's OWN resolveForecastOwnership()
      // call). This is the root cause identified during Rev.1 validation
      // (ADD_Forecast_Architecture.md SS15 buildForecastStructure() header):
      // any external consumer reading resourceRows[].children (e.g.
      // buildForecastStructure()) saw an empty array regardless of how many
      // real Sub Codes existed, because the real Sub Code data
      // (subsOfThisResource) was never attached to the object this function
      // actually returns. Adding it here is purely additive — no existing
      // field, and no existing behavior of buildCumulativeData() itself
      // (which never read this field back from resourceRows), changes.
      return { ...res, ...resAgg, eac: resourceEac, bac: resourceBac, earnedValue: resourceEv, cpi: resourceCpi,
        varianceCompletion: resourceVarianceCompletion, varianceEac: resourceVarianceEac,
        isResource: true, subCount: subsOfThisResource.length, resource_type: rtype,
        children: subsOfThisResource }
    })

    // Legacy: budget for Sub Codes not linked to any Resource Code (orphans) still
    // counts toward the Cost Code's bac directly (kept for backward compatibility)
    const legacySubBudgets = subBudgetMap[parent.id] || {}
    const hasLegacySubBudgets = Object.keys(legacySubBudgets).length > 0
    const orphanSubBac = hasLegacySubBudgets
      ? subRows.filter(s => !resourceIds.has(s.parent_id)).reduce((s, r) => s + (r.bac || 0), 0)
      : 0

    // Cost Code bac = sum of its Resource Codes' budgets (+ any legacy orphan Sub Code budgets)
    const resourceBacSum = resourceRows.reduce((s, r) => s + (r.bac || 0), 0)
    parentAgg.bac += resourceBacSum + orphanSubBac

    // Cost Code Earned Value = sum of its Resource Codes' EV (Direct case) —
    // plus whatever came in via parentDirect above (Indirect case, where EV
    // lives directly on the Cost Code, since Indirect has no Resource Code
    // level). Never recalculated independently — always a roll-up of children.
    const resourceEvSum = resourceRows.reduce((s, r) => s + (r.earnedValue || 0), 0)
    parentAgg.earnedValue += resourceEvSum

    // Recalc parent variances
    parentAgg.varianceEac = parentAgg.eac - parentAgg.prevEac
    parentAgg.varianceCompletion = parentAgg.bac - parentAgg.eac
    // CPI is always derived here, from this row's own rolled-up
    // earnedValue/totalActualCost — never summed or recalculated independently.
    parentAgg.cpi = (parentAgg.earnedValue && parentAgg.totalActualCost) ? (parentAgg.earnedValue / parentAgg.totalActualCost) : 0

    // Attach resource_id on each sub row's parent grouping so the table can nest them correctly
    const subRowsWithGrouping = subRows.map(s => ({ ...s, isUnderResource: resourceIds.has(s.parent_id) }))

    return { ...parent, ...parentAgg, isParent: true, children: subRowsWithGrouping,
      resourceRows, packageNames: ccPackageNames[parent.id] || []
    }
  }

  const directRows = directParents.map(buildParentRow)
  const indirectRows = indirectParents.map(buildParentRow)

  // Group Indirect Cost Codes by their user-defined Category (if any exist).
  // A Cost Code with no category assigned falls into an "Uncategorized" bucket.
  // If the project has zero categories defined, indirectCategories stays empty
  // and the UI falls back to the single "Indirect" tab (unchanged behavior).
  const indirectCategories = (indirectCategoriesData || []).map(cat => ({
    ...cat,
    rows: indirectRows.filter(r => r.indirect_category_id === cat.id),
  }))
  const uncategorizedIndirectRows = indirectRows.filter(r =>
    !r.indirect_category_id || !indirectCategories.some(c => c.id === r.indirect_category_id)
  )

  // Totals
  const calcTotal = (arr) => {
    const acc = arr.reduce((acc, r) => {
      for (const k of ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac','varianceEac','varianceCompletion','invMaterials','invSubcontracts','invEquipment','invPettyCash','invOthers','invSalaries','earnedValue']) {
        acc[k] = (acc[k] || 0) + (r[k] || 0)
      }
      return acc
    }, {})
    // CPI is always derived from the rolled-up earnedValue/totalActualCost at
    // whatever level this total represents (Package, Direct, Indirect, Grand) —
    // never summed or averaged from child CPI values directly.
    acc.cpi = (acc.earnedValue && acc.totalActualCost) ? (acc.earnedValue / acc.totalActualCost) : 0
    return acc
  }

  const directTotals = calcTotal(directRows)
  const indirectTotals = calcTotal(indirectRows)
  const grandTotals = calcTotal([directTotals, indirectTotals])

  // Per-category totals for the Summary tab and each category's own tab header
  const indirectCategoriesWithTotals = indirectCategories.map(cat => ({
    ...cat,
    totals: calcTotal(cat.rows),
  }))
  const uncategorizedTotals = calcTotal(uncategorizedIndirectRows)

  // Resource-type totals (Direct only) — sum every Resource Code across all Cost Codes,
  // grouped by its resource_type. Budget now lives on the Resource Code itself (not Sub Codes).
  const resourceTypeTotals = { material: {}, labour: {}, subcontract: {}, equipment: {} }
  for (const parent of directRows) {
    for (const res of (parent.resourceRows || [])) {
      const rtype = res.resource_type
      if (!rtype || !resourceTypeTotals[rtype]) continue
      for (const k of ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac','earnedValue']) {
        resourceTypeTotals[rtype][k] = (resourceTypeTotals[rtype][k] || 0) + (res[k] || 0)
      }
    }
  }
  for (const rtype of Object.keys(resourceTypeTotals)) {
    const t = resourceTypeTotals[rtype]
    t.cpi = (t.earnedValue && t.totalActualCost) ? (t.earnedValue / t.totalActualCost) : 0
  }

  // Gross Profit / Budget Profit — Contract Value vs Forecast/Budget.
  // Computed once here from the same grandTotals.eac/bac already derived
  // above — never recalculated independently by any consumer.
  const grossProfit = contractValue - grandTotals.eac
  const budgetProfit = contractValue - grandTotals.bac

  // Expose project-level metrics both under `meta` (grouped, for clarity)
  // and spread onto grandTotals (for consumers that expect a single flat
  // object of "the numbers", matching how bac/commitment/etc. are already
  // read off grandTotals throughout Cost Control and Executive Report).
  grandTotals.cashInTotal = cashInTotal
  grandTotals.cashOutTotal = cashOutTotal
  grandTotals.netCash = netCash
  grandTotals.grossProfit = grossProfit
  grandTotals.budgetProfit = budgetProfit

  const meta = { contractValue, cashInTotal, cashOutTotal, netCash, grossProfit, budgetProfit }

  return { directRows, indirectRows, directTotals, indirectTotals, grandTotals, resourceTypeTotals,
    indirectCategories: indirectCategoriesWithTotals, uncategorizedIndirectRows, uncategorizedTotals, meta }
}

// ─── buildCumulativeSeries() ────────────────────────────────────────────────
// Phase 4 — Time-Series & Curve Engine. The official Owner for producing a
// Financial Snapshot time-series across multiple forecastDate points.
//
// Runs extractFetchedData()'s 22 project-invariant queries EXACTLY ONCE,
// then computes one snapshot per requested forecastDate from that same
// in-memory dataset via computeSnapshotFromFetchedData() — the identical
// calculation path buildCumulativeData() itself uses for a single point.
// No financial logic is duplicated or reimplemented here.
//
// asOfForecastDate remains the only mechanism for "a specific point in
// time" — this function is purely an extension of that existing mechanism
// to multiple points, not a new definition of any financial fact.
//
// selectedMonth/selectedYear are not meaningful across a multi-point
// series (they scope "this period" splits like actualThisPeriod to ONE
// calendar month) — always passed as null here, matching how
// computeHistoricalSnapshot() already calls the single-point path for the
// same reason.
export async function buildCumulativeSeries(projectId, forecastDates) {
  if (!projectId) return []

  // Validation — invalid input is rejected explicitly, never silently
  // dropped. null/undefined/empty-string entries and a non-array input are
  // all rejected with a clear error identifying what was wrong, per the
  // approved Phase 4 design (no silent tolerance of bad input).
  if (!Array.isArray(forecastDates)) {
    throw new Error('buildCumulativeSeries: forecastDates must be an array')
  }
  if (forecastDates.length === 0) {
    throw new Error('buildCumulativeSeries: forecastDates must not be empty')
  }
  const invalidEntries = forecastDates.filter(d => d == null || (typeof d === 'string' && d.trim() === ''))
  if (invalidEntries.length > 0) {
    throw new Error(
      `buildCumulativeSeries: forecastDates contains ${invalidEntries.length} null/undefined/empty ` +
      `entry(ies) — every entry must be a valid forecast_date string.`
    )
  }

  // Duplicates removed deterministically, preserving first-occurrence
  // order — a caller passing the same date twice (e.g. built from two
  // merged sources) gets one snapshot per unique date, in the order those
  // dates first appeared, not an arbitrary Set-iteration order.
  const seen = new Set()
  const uniqueDates = []
  for (const d of forecastDates) {
    if (seen.has(d)) continue
    seen.add(d)
    uniqueDates.push(d)
  }

  const fetchedData = await extractFetchedData(projectId)

  // A forecastDate with no matching forecast_history rows is explicitly
  // ALLOWED (not an error) — computeSnapshotFromFetchedData() falls back to
  // the same Not-Forecasted Budget resolution buildCumulativeData() already
  // uses when asOfForecastDate matches nothing, per the approved design.
  // The point still appears in the series with that fallback-derived
  // snapshot, never silently dropped.
  return uniqueDates.map(forecastDate => ({
    forecastDate,
    ...computeSnapshotFromFetchedData(fetchedData, null, null, forecastDate),
  }))
}



// ─── Historical Snapshot (Trend Indicators) ──────────────────────────────────
// Thin wrapper, NOT a second calculation engine. Delegates entirely to
// buildCumulativeData() with asOfForecastDate set. See the documented
// limitation above buildCumulativeData(): only EAC (and metrics derived from
// it — VAC, CPI, EV variance, Gross Profit, Forecast Change) are truly "as of"
// forecastDate. BAC/Commitment/Actual remain current-to-date values until
// their own historical snapshot mechanism exists.
export async function computeHistoricalSnapshot(projectId, forecastDate) {
  return buildCumulativeData(projectId, null, null, forecastDate)
}

// ─── flattenForSnapshot — the ONLY place that prepares rows for
// commit_forecast_snapshot. Neither DirectForecast.jsx nor
// IndirectForecast.jsx contain any snapshot-building logic of their own —
// both call this one function and send its output straight to the RPC.
//
// Inputs:
//   - source: 'direct' | 'indirect'
//   - projectId
//   - draftEdits: { [row_key]: { eac_amount, etc_amount?, method, manual_amount?, qty?, rate? } }
//       Local, unsaved edits the user made in this forecasting session —
//       these override the corresponding row's live eac/etc for the
//       snapshot being committed. Every row NOT present in draftEdits is
//       included at its current live value, exactly as buildCumulativeData()
//       already computes it (same Not-Forecasted-per-row rule as the screens
//       — see resourceEac()/ccEacFromRows() — so partial-forecast scenarios
//       stay correct here too, since eac/totalActualCost/bac/commitment on
//       each row already reflect that rule).
//
// Output: a flat array of plain objects, one per forecastable row (every
// Sub Code, every Resource Code for 'direct', every Sub Code/Cost Code for
// 'indirect') — exactly the shape commit_forecast_snapshot's p_rows expects.
// This is what makes a snapshot "project-wide and atomic": every row in the
// project is captured at the exact same instant this function runs, not just
// the rows the user happened to edit.
// ─────────────────────────────────────────────────────────────────────────
// buildForecastStructure() — the raw Forecast domain hierarchy.
// (ADD_Forecast_Architecture.md SS15 — Single Draft Resolution Engine,
// domain-modeling refinement: separating structure from resolved values.)
//
// ROOT CAUSE THIS FUNCTION FIXES: buildCumulativeData() is, by design, a
// RESOLVED financial model — it already applies its own internal
// resolveForecastOwnership() to produce a final `eac` per node, for its
// own purpose (live/historical reporting). resolveDraft() needs the
// opposite: a RAW domain structure (Cost Codes, Resources, Sub Codes,
// Budget, Actual, Commitment, and parent/child relationships only) with
// NO Forecast resolution, NO Ownership, and NO EAC calculation baked in
// — so it can apply ITS OWN resolution (Baseline + Current Decisions +
// Ownership) from a clean slate. Passing buildCumulativeData()'s already-
// resolved `eac` into resolveDraft() silently reintroduces
// buildCumulativeData()'s OWN ownership logic underneath resolveDraft()'s,
// which is exactly the bug this function exists to eliminate: a Resource
// whose only real Forecast decision lived on a child Sub Code resolved to
// buildCumulativeData()'s pre-computed Budget-fallback value instead of
// resolveDraft() being given the chance to aggregate that child's decision
// itself.
//
// DESIGN: rather than duplicating buildCumulativeData()'s extensive
// Supabase querying (BAC from tender_direct_items, Commitment/Actual
// aggregation, EV, etc. — a large amount of business logic that must
// never be re-implemented a second time, per the No-Duplicated-Business-
// Logic principle), this function calls buildCumulativeData() ONCE and
// derives the raw structure from its per-node bac/totalActualCost/
// commitment/code fields — which are themselves NOT affected by
// buildCumulativeData()'s internal ownership resolution (only the `eac`
// field is). This is not "two hierarchies bridged by an adapter" — it is
// ONE hierarchy, with the one contaminated field (the pre-resolved `eac`)
// removed, so resolveDraft() always starts from zero Forecast assumptions
// regardless of what buildCumulativeData() itself would have concluded.
//
// PURITY: no independent Supabase calls of its own beyond the one
// buildCumulativeData() call it wraps.
//
// OUTPUT: the same Cost Code / Resource / Sub Code tree shape
// buildCumulativeData() returns (resourceRows[].children[], parent.children[]),
// with bac/totalActualCost/commitment/code preserved on every node exactly
// as buildCumulativeData() computed them, and NO `eac` field present at
// all — so resolveDraft() can never accidentally read a pre-resolved value.
export async function buildForecastStructure(projectId, source) {
  const cumulative = await buildCumulativeData(projectId)
  const rows = source === 'direct' ? cumulative.directRows : cumulative.indirectRows

  function stripResolvedEac(node) {
    const { eac, ...structural } = node
    return structural
  }

  return rows.map(parent => ({
    ...stripResolvedEac(parent),
    resourceRows: (parent.resourceRows || []).map(res => ({
      ...stripResolvedEac(res),
      children: (res.children || []).map(stripResolvedEac),
    })),
    children: (parent.children || []).map(stripResolvedEac),
  }))
}

// ─────────────────────────────────────────────────────────────────────────
// resolveDraft() — the single Draft resolution engine.
// (ADD_Forecast_Architecture.md SS15 — Single Draft Resolution Engine.)
//
// CONTRACT: this function owns exactly one responsibility — applying
// Baseline + Current Draft Decisions + Ownership on top of the raw
// Forecast Domain Structure buildForecastStructure() produces, to yield
// the Resolved Draft Model. It never recomputes or re-queries anything
// buildForecastStructure() already owns (Budget/Actual/Commitment/
// structure pass through untouched).
//
// PURITY: this is a pure function. No Supabase calls, no queries, no
// reads from React state, no side effects of any kind. Every input is a
// plain argument; the output is a plain return value. This is what lets
// both the UI (DirectForecast.jsx/IndirectForecast.jsx) and
// flattenForSnapshot() call it identically, with no risk of the two
// callers silently diverging — which is exactly the bug this function
// exists to eliminate permanently (see SS15.1's root-cause trace, and the
// buildForecastStructure() header above for the deeper root cause found
// during Rev.1 validation on the reset dataset).
//
// OWNERSHIP APPLIES TO EAC ONLY (SS15.3): Budget/Actual/Commitment are
// never touched by ownership resolution — they pass through from
// `cumulative` as pure, unconditional aggregates. Only the
// `resolvedEac`/`isOwner`/`hasEffectiveForecast` fields on each returned
// node are new; every other field on each node is copied through from
// `cumulative` unchanged.
//
// INPUTS:
//   cumulative       — the tree from buildForecastStructure() (a raw
//                       domain structure, NOT buildCumulativeData()'s
//                       resolved model) — taken as-is, never re-derived.
//   baselineRows     — { [row_key]: { eac_amount, ... } } — the source
//                       Revision's frozen values (empty object if this

//                       Draft has no Baseline, e.g. not created via
//                       Create Revision).
//   currentDecisions — { [row_key]: { eac_amount, ... } } — the merged
//                       Current Draft Decisions (draftPersistedRows +
//                       draftEdits in today's UI terms). The ONLY source
//                       of ownership for EAC purposes — Baseline never
//                       grants ownership, per the agreed UX/ownership rule.
//
// OUTPUT: a new tree, same shape as `cumulative`'s rows (parent Cost Codes,
// each with .resourceRows and/or .children), where every node additionally
// carries:
//   .resolvedEac   — the resolved Forecast (EAC) value for this node.
//   .isOwner       — true if this node's resolvedEac came from a real
//                    Current Draft Decision on this exact node; false if
//                    it came from aggregating children, from the
//                    Baseline, or from Budget fallback.
// All other fields on each node (bac, totalActualCost, commitment, code,
// description, etc.) are copied through unchanged from `cumulative`.
//
// RESOLUTION RULE, applied uniformly at every level (Sub Code, Resource,
// Cost Code) — see ADD SS15.4 for the full rationale:
//   1. A real Current Draft Decision on this exact node → Owner; use that
//      decision's eac_amount directly. Aggregation from children never
//      happens for an Owner node, regardless of what its children resolve to.
//   2. No Current Decision, and this is a leaf (no children/resourceRows):
//      fall through to the Baseline's eac_amount for this row_key if one
//      exists (display value only — does NOT set isOwner); otherwise fall
//      through to this node's own Budget (bac).
//   3. No Current Decision, and this node has children/resourceRows:
//      resolve every child first; if ANY child resolved to isOwner=true
//      (at any depth, since a Resource's children resolve recursively
//      through this same rule), aggregate = sum of children's resolvedEac.
//      Otherwise (no descendant has a real decision), fall through to this
//      node's own Budget (bac) — matching the pre-existing "nothing
//      forecasted anywhere under here" fallback behavior.
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// normalizeDecisions() — enforces the ownership domain invariant.
// (ADD_Forecast_Architecture.md SS16 — Ownership Normalization.)
//
// DOMAIN RULE (SS16.2): a single ownership chain must never contain two
// EFFECTIVE decisions. A Resource-level decision and a decision on one of
// its own Sub Codes answer the same underlying question ("what is the EAC
// here?") — they cannot both be effective simultaneously.
//
// WHEN THIS RUNS (SS16.3/16.6): during editing, a Draft MAY transiently
// hold a decision on a Resource and on one or more of its Sub Codes at the
// same time — this requires no validation, no blocking, no warning, and
// is a completely normal editing workflow. The conflict is resolved
// exactly ONCE, at the moment the Draft becomes a coherent persisted
// state — immediately before Save or Commit, by calling this function on
// the raw decision set before passing it to resolveDraft().
//
// RULE, PRECISELY (SS16.5): if any descendant of a node (a Sub Code under
// a Resource, or a Resource/orphan-Sub-Code under a Cost Code) has a
// decision, any decision on the ANCESTOR node itself is removed from the
// returned decision set. This is NOT a general "most granular always
// wins" precedence rule re-applied on every read — it is a one-time
// cleanup applied once, here, so resolveDraft() always receives an
// already-consistent decision set and requires no conflict-resolution
// logic of its own.
//
// PURITY: pure function — no Supabase calls, no side effects. Takes the
// raw decision set and the Forecast domain structure (from
// buildForecastStructure()) as plain arguments, returns a new normalized
// decision set as a plain object.
//
// PIPELINE POSITION (SS16.4):
//   Draft Decisions -> normalizeDecisions() -> resolveDraft() ->
//   flattenForSnapshot() -> save_draft()/commit_forecast_draft()
export function normalizeDecisions(rawDecisions, structure) {
  const normalized = { ...rawDecisions }

  for (const parent of structure) {
    for (const res of (parent.resourceRows || [])) {
      const childHasDecision = (res.children || []).some(c => normalized[(c.code || '').trim()] != null)
      const resKey = (res.code || '').trim()
      if (childHasDecision && normalized[resKey] != null) {
        delete normalized[resKey]
      }
    }
    const childHasDecisionAtCcLevel =
      (parent.resourceRows || []).some(r => normalized[(r.code || '').trim()] != null) ||
      (parent.children || []).some(c => normalized[(c.code || '').trim()] != null)
    const parentKey = (parent.code || '').trim()
    if (childHasDecisionAtCcLevel && normalized[parentKey] != null) {
      delete normalized[parentKey]
    }
  }

  return normalized
}

export function resolveDraft(cumulative, baselineRows = {}, currentDecisions = {}) {
  function keyOf(code) { return (code || '').trim() }

  // Three independent concepts (ADD_Forecast_Architecture.md SS15.4 —
  // discovered and formalized after an initial single-boolean design
  // produced a real bug: a Resource whose only child carried an inherited
  // Baseline, not a Current Decision, failed to aggregate and incorrectly
  // fell back to Budget instead of summing the child's Baseline value):
  //
  //   - isOwner              — true ONLY when this exact node has a
  //                            Current Decision. Drives the UI forecast
  //                            indicator (•) only. Never true from a
  //                            Baseline, never true from aggregation.
  //   - hasEffectiveForecast — true when this node contributes a real
  //                            value (Current Decision OR Baseline), false
  //                            when it's pure Budget fallback. Drives
  //                            PARENT aggregation eligibility only — a
  //                            parent aggregates if ANY child has this,
  //                            regardless of whether that child isOwner.
  //   - resolvedEac          — the actual Effective Forecast value,
  //                            regardless of which of the above is true.
  //
  // These must never be collapsed back into one boolean — that collapse
  // is exactly what caused the aggregation bug this rewrite fixes.

  function resolveLeaf(node) {
    const key = keyOf(node.code)
    const decision = currentDecisions[key]
    if (decision != null) {
      return { ...node, resolvedEac: parseFloat(decision.eac_amount) || 0, isOwner: true, hasEffectiveForecast: true }
    }
    const baseline = baselineRows[key]
    if (baseline != null) {
      return { ...node, resolvedEac: parseFloat(baseline.eac_amount) || 0, isOwner: false, hasEffectiveForecast: true }
    }
    return { ...node, resolvedEac: node.bac || 0, isOwner: false, hasEffectiveForecast: false }
  }

  function resolveResource(res) {
    const key = keyOf(res.code)
    const decision = currentDecisions[key]
    if (decision != null) {
      // Owner at the Resource level — children are still resolved (so
      // their own resolvedEac/isOwner/hasEffectiveForecast are available
      // for display), but they do not contribute to this Resource's
      // resolvedEac while it owns its own decision directly.
      const resolvedChildren = (res.children || []).map(resolveLeaf)
      return { ...res, children: resolvedChildren, resolvedEac: parseFloat(decision.eac_amount) || 0, isOwner: true, hasEffectiveForecast: true }
    }
    const resolvedChildren = (res.children || []).map(resolveLeaf)
    // Aggregation eligibility: ANY child with a real Effective Forecast —
    // a Current Decision OR a Baseline — is sufficient. A child that is
    // itself pure Budget fallback (hasEffectiveForecast: false) is not.
    const hasAnyEffectiveForecast = resolvedChildren.some(c => c.hasEffectiveForecast)
    const resolvedEac = hasAnyEffectiveForecast
      ? resolvedChildren.reduce((s, c) => s + (c.resolvedEac || 0), 0)
      : (res.bac || 0)
    return { ...res, children: resolvedChildren, resolvedEac, isOwner: false, hasEffectiveForecast: hasAnyEffectiveForecast }
  }

  function resolveParent(parent) {
    const resolvedResourceRows = (parent.resourceRows || []).map(resolveResource)
    const resolvedChildren = (parent.children || []).map(resolveLeaf)
    const key = keyOf(parent.code)
    const decision = currentDecisions[key]
    if (decision != null) {
      return { ...parent, resourceRows: resolvedResourceRows, children: resolvedChildren,
        resolvedEac: parseFloat(decision.eac_amount) || 0, isOwner: true, hasEffectiveForecast: true }
    }
    const hasAnyEffectiveForecast = resolvedResourceRows.some(r => r.hasEffectiveForecast) || resolvedChildren.some(c => c.hasEffectiveForecast)
    const resolvedEac = hasAnyEffectiveForecast
      ? resolvedResourceRows.reduce((s, r) => s + (r.resolvedEac || 0), 0) + resolvedChildren.reduce((s, c) => s + (c.resolvedEac || 0), 0)
      : (parent.bac || 0)
    return { ...parent, resourceRows: resolvedResourceRows, children: resolvedChildren, resolvedEac, isOwner: false, hasEffectiveForecast: hasAnyEffectiveForecast }
  }

  return cumulative.map(resolveParent)
}



// Sub Code, every Resource Code for 'direct', every Sub Code/Cost Code for
// 'indirect') — exactly the shape commit_forecast_snapshot's p_rows expects.
// This is what makes a snapshot "project-wide and atomic": every row in the
// project is captured at the exact same instant this function runs, not just
// the rows the user happened to edit.
//
// ─────────────────────────────────────────────────────────────────────────
// reconstructHierarchyFromSnapshotRows() — the single, generic way to
// rebuild a historical hierarchy from committed forecast_history rows,
// using parent_key alone (ADD_Forecast_Architecture.md SS18). This is
// intentionally NOT specific to Cost Control — it is the shared
// foundation any future historical reporting (Executive Report,
// Dashboards, Cash Flow, etc.) should build on, since every one of them
// will eventually need "what did this historical Revision/Release's
// structure actually look like," not today's live cost_codes structure.
//
// CONTRACT: pure function. Takes a flat array of snapshot rows (as read
// directly from forecast_history — row_key, parent_key, and whatever
// other fields the caller selected) and returns a tree of the SAME row
// objects, each augmented with a `children` array. No Supabase calls, no
// side effects, no knowledge of Direct/Indirect, Cost Control, or any
// other consumer-specific shape. Consumers (e.g. buildCostControlDataFromRelease())
// are responsible for reshaping this generic tree into whatever
// consumer-specific output they need.
//
// DETERMINISM (SS18, this conversation's discussion): a Project Forecast
// Release is an immutable historical record. If a row's parent_key
// references a row_key that does not exist among the snapshot rows
// provided, that is treated as data corruption — NOT a recoverable
// business scenario. This function NEVER fabricates a different tree to
// paper over the gap (e.g. attaching the orphaned row as a new root,
// which would silently change the historical hierarchy and produce a
// report that looks valid but is structurally wrong). Instead, every
// broken reference is collected and reported explicitly in the return
// value's `errors` array — the caller decides how to surface that
// (an error page, a log entry, diagnostics), but reconstruction itself
// never invents structure that didn't exist at Commit time.
//
// RETURN SHAPE:
//   {
//     roots: [...],   // top-level nodes (parent_key === null), each with
//                      // a nested `children` array — empty if the
//                      // hierarchy could not be fully validated (errors
//                      // is non-empty); callers should check `errors`
//                      // before trusting `roots`.
//     errors: [...],   // { row_key, parent_key } for every row whose
//                       // parent_key did not resolve to any row_key
//                       // present in the provided snapshot rows.
//   }
// ─────────────────────────────────────────────────────────────────────────
// resolveCurrentForecastState() — the single source of truth for "what is
// the currently effective, resolved Forecast state for this project right
// now" (ADD_Forecast_Architecture.md — SSOT principle, extended per this
// conversation's explicit conclusion).
//
// WHY THIS EXISTS: Cost Control (Live) was found to compute EAC via a
// completely separate, independent algorithm (resolveForecastOwnership(),
// inside buildCumulativeData()) from the one Direct/Indirect Forecast use
// (resolveDraft(), fed by buildForecastStructure() + normalizeDecisions()).
// Both answer the same underlying business question — "what is the
// effective EAC right now, given whatever Draft/Baseline/Decision state
// exists" — but arrived at genuinely different answers for the same data
// (a Resource with no direct decision, whose only real decision lives on
// a child, silently collapsed to a wrong EAC in one algorithm and not the
// other). Per this conversation's explicit conclusion: this is not a case
// for a new/fixed algorithm in resolveForecastOwnership() — it is a case
// for eliminating the second implementation entirely and having every
// consumer of "current effective EAC" (Cost Control included) consume the
// exact same resolved output resolveDraft() already produces.
//
// CONTRACT: this function does NOT decide business rules of its own. It
// consolidates exactly the Draft-loading + Baseline-loading sequence
// DirectForecast.jsx/IndirectForecast.jsx already perform (getOrCreateDraft,
// read based_on_snapshot_id, load Baseline filtered to is_forecasted=true
// with the forecast_history_decision_flags fallback for pre-migration
// Revisions, load this Draft's own persisted rows), then calls
// buildForecastStructure() + resolveDraft() — the SAME functions the
// Forecast pages call — and returns the resolved tree. Per explicit
// agreement: "Live" is never defined here in terms of Draft vs Revision —
// that distinction is an implementation detail of getOrCreateDraft()
// (which always returns/creates the current Draft, falling back to
// whatever Baseline exists). This function simply asks for "the current
// resolved state" and returns it, exactly as Direct/Indirect Forecast
// would display it at this exact moment.
//
// SIDE EFFECT NOTE: getOrCreateDraft() will CREATE a Draft if none exists
// yet for the current period (same behavior as opening the Forecast page
// itself would trigger) — this is intentional and matches existing
// behavior; it is not a Cost-Control-specific side effect.
// ─────────────────────────────────────────────────────────────────────────
// resolveCurrentForecastState() — Forecast Resolution service (read-only).
// (ADD_Forecast_Architecture.md — SSOT principle, extended per this
// conversation's explicit conclusion.)
//
// SEPARATION OF RESPONSIBILITIES (explicitly agreed):
//   Forecast Resolution (this function) — resolved forecast tree, resolved
//     EAC/ownership, READ-ONLY. Draft identity, staged edits, and save
//     workflows are NOT part of this capability.
//   Forecast Editing (DirectForecast.jsx/IndirectForecast.jsx's own state:
//     draftId, draftPersistedRows, draftEdits, handleSaveDraft(), etc.) —
//     a separate concern. Direct/Indirect Forecast are both an editor and
//     a viewer, so they consume both services. Cost Control is only a
//     viewer, so it consumes only this one.
//
// WHY THIS EXISTS: Cost Control (Live) was found to compute EAC via a
// completely separate, independent algorithm (resolveForecastOwnership(),
// inside buildCumulativeData()) from the one Direct/Indirect Forecast use
// (resolveDraft(), fed by buildForecastStructure() + normalizeDecisions()).
// Both answer the same underlying business question — "what is the
// effective EAC right now" — but arrived at genuinely different answers
// for the same data. Per this conversation's explicit conclusion: this is
// not a case for a new/fixed algorithm in resolveForecastOwnership() — it
// is a case for eliminating the second implementation entirely and having
// every consumer of "current effective EAC" (Cost Control included)
// consume the exact same resolution capability Direct/Indirect Forecast
// consume — not a copy of their workflow, but the same underlying domain
// service, with Direct/Indirect Forecast becoming consumers of it too
// (their own load() functions call this instead of reproducing this
// sequence inline).
//
// CONTRACT: read-only. Returns ONLY the resolved tree — no draftId, no
// forecast_date, no editable state of any kind. A caller that also needs
// to edit (Direct/Indirect Forecast) separately owns its own Draft/editing
// state via the existing getOrCreateDraft()/saveDraft()/etc. functions;
// this function does not replace those, it only replaces the "resolve the
// current state" portion of what those screens used to do inline.
//
// "Live" is never defined here in terms of Draft vs Revision — that
// distinction is an implementation detail of getOrCreateDraft() (which
// always returns/creates the current Draft, falling back to whatever
// Baseline exists). This function simply resolves "the current state" and
// returns it.
export async function resolveCurrentForecastState(projectId, source) {
  const now = new Date()
  const draftId = await getOrCreateDraft(projectId, source, now.getMonth() + 1, now.getFullYear())

  const { data: draftRow } = await supabase.from('forecast_snapshots')
    .select('based_on_snapshot_id').eq('id', draftId).single()

  // Baseline — identical logic to DirectForecast.jsx/IndirectForecast.jsx's
  // own loading (SS15 domain-modeling addendum): a read-only reference to
  // the source Revision, filtered to rows that were a REAL forecast
  // decision there (is_forecasted=true, resolved via the decision-flags
  // fallback table for pre-migration Revisions). Never copied storage.
  let baselineRows = {}
  if (draftRow?.based_on_snapshot_id) {
    const { data: baselineHistoryRows } = await supabase.from('forecast_history')
      .select('row_key, eac_amount, etc_amount, method, manual_amount, qty, rate, is_forecasted')
      .eq('snapshot_id', draftRow.based_on_snapshot_id)

    const { data: flagData } = await supabase
      .from('forecast_history_decision_flags')
      .select('row_key, is_forecasted')
      .eq('snapshot_id', draftRow.based_on_snapshot_id)
    const flagByRowKey = {}
    for (const f of (flagData || [])) flagByRowKey[f.row_key] = f.is_forecasted

    for (const r of (baselineHistoryRows || [])) {
      const resolvedIsForecasted = flagByRowKey[r.row_key] !== undefined
        ? flagByRowKey[r.row_key]
        : (r.is_forecasted === true)
      if (!resolvedIsForecasted) continue
      baselineRows[r.row_key] = { eac_amount: r.eac_amount, method: r.method, manual_amount: r.manual_amount, qty: r.qty, rate: r.rate }
    }
  }

  // This Draft's own persisted rows — the Current Draft Decisions.
  const { data: draftHistoryRows } = await supabase.from('forecast_history')
    .select('row_key, eac_amount, etc_amount, method, manual_amount, qty, rate')
    .eq('snapshot_id', draftId)
  const currentDecisions = {}
  for (const r of (draftHistoryRows || [])) {
    currentDecisions[r.row_key] = { eac_amount: r.eac_amount, method: r.method, manual_amount: r.manual_amount, qty: r.qty, rate: r.rate }
  }

  const structure = await buildForecastStructure(projectId, source)
  // Return value: ONLY the resolved tree. No draftId, no forecast_date —
  // those are editor concerns, not resolution concerns (explicitly agreed).
  return resolveDraft(structure, baselineRows, currentDecisions)
}

// ─────────────────────────────────────────────────────────────────────────
// applyResolvedForecastToReport() — overlays a resolveCurrentForecastState()
// resolved tree's EAC values onto a buildCumulativeData() report tree, by
// matching nodes on `code`. This is domain knowledge (how a Forecast-
// resolved node maps onto a Cost-Control report node), and therefore lives
// here in the Forecast domain layer — NOT inside CostControl.jsx, which
// must only render a fully-prepared report model and must never need to
// know how forecast nodes map to report nodes (explicitly agreed).
//
// DESIGN: this is a GENERIC forecast overlay engine, not an "EAC
// replacer" (explicitly agreed). Its responsibility is: match forecast
// nodes to report nodes by code, then apply every forecast-DERIVED field
// onto the matching report node, leaving every live-financial field
// (bac, commitment, totalActualCost, etc.) completely untouched. Today
// the only forecast-derived field is `eac` (from resolveDraft()'s
// `resolvedEac`). FORECAST_DERIVED_FIELDS below is the single place that
// list grows from in the future (e.g. ETC, an ownership flag exposed to
// the UI, or other forecast metadata) — a future addition changes only
// that table, never the traversal/matching logic below it, and never any
// caller of this function.
//
// CONTRACT:
//   report           — one buildCumulativeData()-shaped array (directRows
//                       or indirectRows), each node carrying its own
//                       structure (`resourceRows[]`/`children[]`) and
//                       whatever live fields buildCumulativeData() computed.
//   resolvedForecast — the array resolveCurrentForecastState() returned
//                       (resolveDraft()'s output shape — same `code`/
//                       `resourceRows[]`/`children[]` nesting as
//                       buildForecastStructure() produces, plus whatever
//                       forecast-derived fields it resolves per node).
//
// PURITY: pure, immutable (returns a new array, never mutates `report` or
// `resolvedForecast`), no Supabase/network calls, no React or UI
// awareness of any kind — CostControl.jsx (or any other caller) is
// invisible to this function.
//
// BEHAVIOR: a report node with no matching resolved node (should not
// happen in practice, since both ultimately derive from the same
// cost_codes/Resources, but handled defensively) keeps its original
// buildCumulativeData() values for every forecast-derived field
// unchanged, rather than being dropped or zeroed.
const FORECAST_DERIVED_FIELDS = {
  // reportFieldName: resolvedForecastFieldName
  eac: 'resolvedEac',
}

// ─────────────────────────────────────────────────────────────────────────
// buildForecastNodeIndex() — flattens a resolveCurrentForecastState()
// resolved tree into a flat Map<code, ForecastNode> lookup, keyed by each
// node's `code` (Cost Code / Resource / Sub Code row_key). A separate,
// reusable concern from applying that index onto any particular report
// (explicitly agreed) — a future consumer (a Project Forecast Release
// view, Executive Report, Dashboard, Exports) can build this index once
// and reuse it across multiple report trees without rebuilding it, and
// applyResolvedForecastToReport() below never needs to know how the index
// was constructed.
//
// PURITY: pure, no side effects, no knowledge of any particular report
// shape beyond `code`/`resourceRows[]`/`children[]` nesting (the same
// shape buildForecastStructure()/resolveDraft() already produce).
export function buildForecastNodeIndex(resolvedTree) {
  const index = new Map()
  function visit(nodes) {
    for (const node of (nodes || [])) {
      const key = (node.code || '').trim()
      if (key) index.set(key, node)
      visit(node.resourceRows)
      visit(node.children)
    }
  }
  visit(resolvedTree)
  return index
}

// ─────────────────────────────────────────────────────────────────────────
// applyResolvedForecastToReport() — overlays forecast-derived fields (per
// FORECAST_DERIVED_FIELDS above) from a pre-built forecast node index (see
// buildForecastNodeIndex()) onto a buildCumulativeData()-shaped report
// tree, matching on `code`. This function does not care how the index was
// built — it only consumes it (explicitly agreed separation of concerns).
//
// DESIGN: a GENERIC forecast overlay engine, not an "EAC replacer."
// FORECAST_DERIVED_FIELDS is the single place the set of overlaid fields
// grows from in the future — a future addition (ETC, an ownership flag,
// other forecast metadata) changes only that table, never this function
// or any of its callers.
//
// CONTRACT:
//   report — one buildCumulativeData()-shaped array (directRows or
//            indirectRows), each node carrying its own structure
//            (`resourceRows[]`/`children[]`) and whatever live fields
//            buildCumulativeData() computed.
//   index  — a Map<code, ForecastNode> from buildForecastNodeIndex().
//
// PURITY: pure, immutable (returns a new array, never mutates `report` or
// the index), no Supabase/network calls, no React or UI awareness of any
// kind — CostControl.jsx (or any other caller) is invisible to this
// function.
//
// BEHAVIOR: a report node with no matching index entry (should not happen
// in practice, since both ultimately derive from the same cost_codes/
// Resources, but handled defensively) keeps its original
// buildCumulativeData() values for every forecast-derived field
// unchanged, rather than being dropped or zeroed.
export function applyResolvedForecastToReport(report, index) {
  function overlayNode(node) {
    const key = (node.code || '').trim()
    const resolvedNode = index.get(key)
    const overlaidFields = {}
    for (const [reportField, resolvedField] of Object.entries(FORECAST_DERIVED_FIELDS)) {
      const resolvedValue = resolvedNode ? resolvedNode[resolvedField] : undefined
      overlaidFields[reportField] = resolvedValue != null ? resolvedValue : node[reportField]
    }
    // #8: the two ROW-level fields that buildCumulativeData() derives from `eac` (varianceEac = eac - prevEac,
    // varianceCompletion = bac - eac — the same formulas used at every level above) must follow the overlaid
    // eac, otherwise a row would show the NEW eac next to a VARIANCE computed from the OLD one. Only done when
    // the eac was really replaced by the resolved Draft state; an unmatched node keeps every value untouched.
    if (resolvedNode && resolvedNode[FORECAST_DERIVED_FIELDS.eac] != null) {
      overlaidFields.varianceEac = (overlaidFields.eac || 0) - (node.prevEac || 0)
      overlaidFields.varianceCompletion = (node.bac || 0) - (overlaidFields.eac || 0)
    }
    return {
      ...node,
      ...overlaidFields,
      resourceRows: (node.resourceRows || []).map(overlayNode),
      children: (node.children || []).map(overlayNode),
    }
  }

  return (report || []).map(overlayNode)
}

// ─────────────────────────────────────────────────────────────────────────
// #8 — SHARED "live Draft state" transformation (Executive Report ↔ Cost Control consistency)
//
// Until now Cost Control (Live) applied the current Draft forecast state to buildCumulativeData()'s
// output INSIDE ITS OWN PAGE (overlay + recalcTotal + resourceType/category re-derivation), Dashboard
// repeated the overlay, and Executive Report (Live) applied none of it, so the same project showed a
// different EAC / VAC / profit on different screens as soon as a Draft existed.
//
// This is the ONE place that does it now. It is a pure consumer-layer transformation of the existing
// SSOT output (it never fetches, never mutates its inputs, introduces no new financial formula):
//   1. applyResolvedForecastToReport() above overlays eac (and re-derives the 2 row fields that depend on it);
//   2. totals are re-summed from the overlaid rows with the SAME field list and the SAME derived
//      relations (cpi = earnedValue / totalActualCost, varianceCompletion = bac - eac,
//      varianceEac = eac - prevEac) that CostControl.jsx used inline;
//   3. grossProfit = contractValue - eac with contractValue taken from the SSOT's own meta
//      (buildCumulativeData() read it from projects.contract_value), not from any page state.
// Release / frozen reports never go through this: they keep using buildCostControlDataFromRelease().
const LIVE_SUM_FIELDS = ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac','varianceEac','varianceCompletion','invMaterials','invSubcontracts','invEquipment','invPettyCash','invOthers','invSalaries','earnedValue']

export function recalcTotalsFromRows(rows) {
  const acc = (rows || []).reduce((acc, r) => {
    for (const k of LIVE_SUM_FIELDS) acc[k] = (acc[k] || 0) + (r[k] || 0)
    return acc
  }, {})
  acc.cpi = (acc.earnedValue && acc.totalActualCost) ? (acc.earnedValue / acc.totalActualCost) : 0
  acc.varianceCompletion = (acc.bac || 0) - (acc.eac || 0)
  acc.varianceEac = (acc.eac || 0) - (acc.prevEac || 0)
  return acc
}

export function applyLiveForecastToCumulative(cumulative, resolvedDirect, resolvedIndirect) {
  const directRows = applyResolvedForecastToReport(cumulative.directRows, buildForecastNodeIndex(resolvedDirect))
  const indirectRows = applyResolvedForecastToReport(cumulative.indirectRows, buildForecastNodeIndex(resolvedIndirect))

  const directTotals = recalcTotalsFromRows(directRows)
  const indirectTotals = recalcTotalsFromRows(indirectRows)
  const grandTotals = recalcTotalsFromRows([directTotals, indirectTotals])
  // project-level metrics that do not depend on the EAC overlay are carried over untouched
  grandTotals.cashInTotal = cumulative.grandTotals.cashInTotal
  grandTotals.cashOutTotal = cumulative.grandTotals.cashOutTotal
  grandTotals.netCash = cumulative.grandTotals.netCash
  grandTotals.grossProfit = ((cumulative.meta && cumulative.meta.contractValue) || 0) - grandTotals.eac
  grandTotals.budgetProfit = cumulative.grandTotals.budgetProfit

  const resourceTypeTotals = { material: {}, labour: {}, subcontract: {}, equipment: {} }
  for (const parent of directRows) {
    for (const res of (parent.resourceRows || [])) {
      const rtype = res.resource_type
      if (!rtype || !resourceTypeTotals[rtype]) continue
      for (const k of LIVE_SUM_FIELDS) {
        resourceTypeTotals[rtype][k] = (resourceTypeTotals[rtype][k] || 0) + (res[k] || 0)
      }
    }
  }
  for (const rtype of Object.keys(resourceTypeTotals)) {
    const t = resourceTypeTotals[rtype]
    t.cpi = (t.earnedValue && t.totalActualCost) ? (t.earnedValue / t.totalActualCost) : 0
    t.varianceCompletion = (t.bac || 0) - (t.eac || 0)
    t.varianceEac = (t.eac || 0) - (t.prevEac || 0)
  }

  // every collection DERIVED from indirectRows (categories + uncategorized) is re-derived from the overlaid rows
  const indirectCategories = (cumulative.indirectCategories || []).map(({ rows: _staleRows, totals: _staleTotals, ...meta }) => {
    const rows = indirectRows.filter(r => r.indirect_category_id === meta.id)
    return { ...meta, rows, totals: recalcTotalsFromRows(rows) }
  })
  const uncategorizedIndirectRows = indirectRows.filter(r =>
    !r.indirect_category_id || !indirectCategories.some(c => c.id === r.indirect_category_id)
  )
  const uncategorizedTotals = recalcTotalsFromRows(uncategorizedIndirectRows)

  return {
    ...cumulative,
    directRows,
    indirectRows,
    directTotals,
    indirectTotals,
    grandTotals,
    hasDirectSnapshot: true,
    hasIndirectSnapshot: true,
    resourceTypeTotals,
    indirectCategories,
    uncategorizedIndirectRows,
    uncategorizedTotals,
    meta: { ...(cumulative.meta || {}), grossProfit: grandTotals.grossProfit },
  }
}

export function reconstructHierarchyFromSnapshotRows(snapshotRows) {
  const nodesByKey = {}
  for (const row of (snapshotRows || [])) {
    const key = (row.row_key || '').trim()
    if (!key) continue
    nodesByKey[key] = { ...row, children: [] }
  }

  const errors = []
  const roots = []

  for (const row of (snapshotRows || [])) {
    const key = (row.row_key || '').trim()
    if (!key) continue
    const node = nodesByKey[key]
    const parentKey = row.parent_key ? row.parent_key.trim() : null

    if (parentKey === null || parentKey === '') {
      roots.push(node)
      continue
    }

    const parentNode = nodesByKey[parentKey]
    if (!parentNode) {
      // Data corruption per this function's contract (SS18) — never
      // silently attach as a root, which would fabricate a different
      // historical hierarchy than actually existed at Commit time.
      errors.push({ row_key: key, parent_key: parentKey })
      continue
    }
    parentNode.children.push(node)
  }

  // If any inconsistency was found, the caller must be told explicitly —
  // roots is still returned (partially reconstructed, minus the orphaned
  // subtrees) so a caller that wants to proceed anyway with a visible
  // warning can, but errors being non-empty must never be silently ignored.
  return { roots, errors }
}

// Per ADD_Forecast_Architecture.md SS15: this function is a CONSUMER of
// resolveDraft(), exactly like the UI is. It performs NO independent EAC
// calculation of its own — it calls buildForecastStructure() to get the
// raw domain hierarchy (never buildCumulativeData()'s own resolved model,
// which contaminates resolveDraft()'s ownership resolution with
// buildCumulativeData()'s own internal ownership logic — see
// buildForecastStructure()'s header for the full root-cause explanation),
// then calls resolveDraft() with the same kind of inputs (structure,
// baselineRows, currentDecisions) that the UI resolves against, and simply
// flattens resolveDraft()'s output tree into forecast_history rows. This
// is what guarantees the committed Snapshot's values are identical to
// whatever the UI displayed immediately before Commit — there is exactly
// one place EAC and ownership are decided, not two.
export async function flattenForSnapshot(projectId, source, currentDecisions = {}, baselineRows = {}) {
  const structure = await buildForecastStructure(projectId, source)
  const rows = resolveDraft(structure, baselineRows, currentDecisions)

  // Indirect Category name lookup (ADD_Forecast_Architecture.md SS19.2c) —
  // only relevant for source='indirect'. indirect_category_id itself
  // survives on each Cost Code node already (it passes through
  // buildCumulativeData()'s ...parent spread and buildForecastStructure()'s
  // stripResolvedEac(), which only strips `eac`); the id->name lookup is
  // the one piece not already present on the node, since the category's
  // name lives in a separate table (indirect_categories), not on
  // cost_codes itself.
  let indirectCategoryNameById = {}
  if (source === 'indirect') {
    const { data: cats } = await supabase.from('indirect_categories').select('id, name').eq('project_id', projectId)
    for (const c of (cats || [])) indirectCategoryNameById[c.id] = c.name
  }

  const out = []
  const seenRowKeys = new Set()

  // parentKey (ADD_Forecast_Architecture.md SS18): the row_key of this
  // row's parent in the hierarchy AT THE TIME OF THIS COMMIT — null for a
  // top-level Cost Code. This is the only additional structural metadata
  // needed to reconstruct a historical hierarchy from a committed
  // Revision/Release without depending on today's live cost_codes table
  // (SS18.2/18.3 — no separate node-type column needed; the hierarchy is
  // fully described by parent pointers alone).
  //
  // resourceType (ADD_Forecast_Architecture.md SS19): the Resource's
  // material/labour/subcontract/equipment classification AT THE TIME OF
  // THIS COMMIT — only meaningful (non-null) for Resource-level rows.
  // Per SS19.1's general principle: presentation-affecting metadata that
  // is not guaranteed immutable after creation must be frozen into the
  // snapshot, exactly like parent_key.
  //
  // liveAccrual (ADD_Forecast_Architecture.md SS19.2b): the accrued-but-
  // not-yet-invoiced cost component AT THE TIME OF THIS COMMIT. Already
  // folded into liveActual (totalActualCost = actualTotal + accrual,
  // buildCumulativeData()) — so it contributes nothing new to any
  // financial total. Stored separately purely so the historical Actual/
  // Accrual presentation breakdown can be faithfully reproduced later,
  // per SS19.2b's clarification that presentation fidelity, not just
  // calculation correctness, determines what belongs in the snapshot.
  // indirectCategoryId/indirectCategoryName (ADD_Forecast_Architecture.md
  // SS19.2c): the Indirect Category this Cost Code belonged to AT THE TIME
  // OF THIS COMMIT — null for Direct (Direct has no category concept) and
  // for any Indirect Cost Code with no category assigned. Written onto
  // EVERY row for this Cost Code (the Cost Code's own row, every Resource,
  // and every Sub Code beneath it) — not just the Cost Code's own row —
  // so any single row is self-contained and answerable ("what category am
  // I in") without joining back to its parent, per the same principle
  // already applied to parent_key/resource_type (explicitly agreed).
  function pushRow(rowKey, subCode, costCode, resolvedEac, isOwner, liveActual, liveBac, liveCommitment, parentKey, resourceType, liveAccrual, indirectCategoryId, indirectCategoryName, liveEarnedValue) {
    const key = (rowKey || '').trim()
    if (!key || seenRowKeys.has(key.toLowerCase())) return // guards against any accidental duplicate row_key before it ever reaches the RPC
    seenRowKeys.add(key.toLowerCase())
    const decision = isOwner ? currentDecisions[key] : null
    const eac = resolvedEac || 0
    const actual = liveActual || 0
    out.push({
      row_key: key,
      sub_code: subCode || null,
      cost_code: costCode || null,
      parent_key: parentKey || null,
      resource_type: resourceType || null,
      snapshot_indirect_category_id: indirectCategoryId || null,
      snapshot_indirect_category_name: indirectCategoryName || null,
      eac_amount: eac,
      // ev_amount — Earned Value AT THE TIME OF THIS COMMIT. Per this
      // conversation's conclusion: EV is not derived inside Cost Control —
      // it is a business value already persisted by the Budget module
      // (direct_budget_ev/indirect_budget_ev), and buildCumulativeData()
      // (reached here via buildForecastStructure()) already reads it
      // as-is. This freezes that already-persisted value into the
      // snapshot, exactly like snapshot_budget/snapshot_actual/eac_amount
      // above — not a newly-derived field, a frozen copy of an existing
      // one, so a Release remains fully self-contained without needing to
      // rejoin direct_budget_ev/indirect_budget_ev later.
      ev_amount: liveEarnedValue || 0,
      // ETC is always EAC - Actual, derived here in the one shared place —
      // never recalculated by the UI, never stored as an independent input.
      etc_amount: eac - actual,
      method: decision ? (decision.method || null) : null,
      manual_amount: decision ? (decision.manual_amount ?? null) : null,
      qty: decision ? (decision.qty ?? null) : null,
      rate: decision ? (decision.rate ?? null) : null,
      snapshot_actual: actual,
      snapshot_budget: liveBac || 0,
      snapshot_commitment: liveCommitment || 0,
      snapshot_accrual: liveAccrual || 0,
      snapshot_inputs: decision?.snapshot_inputs || {},
      // Decision Layer / Snapshot Layer discriminator (ADD_Forecast_Architecture.md
      // SS4.1 / SS5.1). true only when this row has a real, user-entered
      // decision this cycle. false when eac_amount was populated purely
      // from live Budget-fallback / sum-of-children resolution, with no
      // decision behind it. This is the ONLY signal ownership must be read
      // from at read time — never row existence, never eac_amount being
      // non-zero. isOwner here comes directly from resolveDraft() — the
      // SAME resolution the UI displayed — never recomputed independently.
      is_forecasted: isOwner === true,
    })
  }

  for (const parent of rows) {
    const catId = parent.indirect_category_id || null
    const catName = catId ? (indirectCategoryNameById[catId] || null) : null

    // Direct: Resource Codes sit between Cost Code and Sub Code.
    for (const res of (parent.resourceRows || [])) {
      pushRow(res.code, null, parent.code, res.resolvedEac, res.isOwner, res.totalActualCost, res.bac, res.commitment, parent.code, res.resource_type, res.accrual, catId, catName, res.earnedValue)
      // Sub Codes under this Resource — each carries its OWN resolved
      // eac/isOwner from resolveDraft(), independent of the Resource's.
      // parent_key here is the RESOURCE's row_key, not the Cost Code's —
      // this is exactly the relationship that could not be reconstructed
      // from sub_code/cost_code alone (SS18.1). Sub Codes never carry
      // their own Earned Value (buildCumulativeData() only attaches EV at
      // the Resource level for Direct — see SS19.2b-equivalent note on
      // earnedValue there), so 0 here is a faithful freeze, not a gap.
      for (const child of (res.children || [])) {
        pushRow(child.code, child.code, parent.code, child.resolvedEac, child.isOwner, child.totalActualCost, child.bac, child.commitment, res.code, null, child.accrual, catId, catName, child.earnedValue)
      }
    }
    // Sub Codes directly under the Cost Code (Indirect, or Direct's legacy
    // orphans not grouped under any Resource). parent_key is the Cost
    // Code's own row_key.
    for (const child of (parent.children || [])) {
      pushRow(child.code, child.code, parent.code, child.resolvedEac, child.isOwner, child.totalActualCost, child.bac, child.commitment, parent.code, null, child.accrual, catId, catName, child.earnedValue)
    }
    // The Cost Code itself. Per Philosophy A (ADD SS4/SS5 — a committed
    // Revision is a complete, self-contained statement), EVERY node gets
    // a Snapshot Layer row, always — not just Cost Codes with no children.
    // The previous conditional here (only writing this row when the Cost
    // Code had no resourceRows/children at all) meant an Indirect Cost
    // Code with Sub Codes never got its own row in forecast_history — so
    // reading a committed Revision found no row for it at all and fell
    // through to 0, not even its live Budget, breaking exactly the
    // completeness guarantee this architecture depends on. parent_key is
    // null here — a Cost Code is always top-level. parent.earnedValue is
    // Direct's Resource-level EV rolled up, or Indirect's own EV (attached
    // directly at the Cost Code level per buildCumulativeData()) — either
    // way, the same figure already displayed on screen for this Cost Code.
    pushRow(parent.code, null, parent.code, parent.resolvedEac, parent.isOwner, parent.totalActualCost, parent.bac, parent.commitment, null, null, parent.accrual, catId, catName, parent.earnedValue)
  }

  return out
}

// ─── loadForecastSnapshot — reads a COMMITTED, immutable snapshot.
// Every value returned comes directly from forecast_history's frozen
// snapshot_actual/snapshot_budget/snapshot_commitment/eac_amount columns —
// nothing here is recalculated from current project data. This is what
// guarantees a historical forecast never changes after it's committed,
// regardless of what happens to Actual/Budget/Commitment/Forecast later.
//
// Only usable for rows with snapshot_id set (i.e. committed through
// commit_forecast_snapshot). Legacy rows (snapshot_id IS NULL) are NOT
// readable through this function — callers should fall back to the
// existing buildCumulativeData(projectId, null, null, asOfForecastDate)
// path for those, exactly as today, per the agreed backward-compatibility
// decision.
//
// Returns a flat array (one entry per row_key) rather than the nested
// directRows/indirectRows tree buildCumulativeData() returns — parent/Cost
// Code/Grand Total aggregation from these flat rows is done by the caller
// (History view), by simply summing snapshot_actual/snapshot_budget/
// snapshot_commitment/eac_amount/etc_amount across the relevant rows. No
// aggregation logic is duplicated here; this function's only job is
// retrieval of the frozen values.
// ─── loadForecastHistory — THE single shared entry point for reading
// forecast_history, for ALL periods of a project/source at once. This is
// the ONLY place that:
//   - distinguishes committed periods (forecast_snapshots exists) from
//     legacy periods (snapshot_id IS NULL, pre-migration data),
//   - applies the immutable-snapshot interpretation (frozen snapshot_actual/
//     snapshot_budget/snapshot_commitment/eac_amount/etc_amount, never
//     recalculated),
//   - applies the legacy fallback marker (committed:false) so callers know
//     a period's Actual/Budget/Commitment are NOT frozen and must still be
//     read from live data, exactly as before this refactor.
//
// DirectForecast.jsx and IndirectForecast.jsx BOTH call this one function
// to populate their forecastHistory state — neither screen queries
// forecast_snapshots or forecast_history directly anymore, and neither
// contains its own snapshot-interpretation logic. Any future consumer
// (Executive Report trend, an audit view, etc.) must also go through this
// function rather than re-deriving the same interpretation independently —
// that duplication is exactly what this refactor exists to prevent.
//
// Tree-building, grouping, and UI formatting remain entirely the screens'
// own responsibility — this function returns a flat, source-of-truth
// per-row-per-period structure; how a screen arranges that into a Cost
// Code / Resource Code / Sub Code tree is presentation, not snapshot logic.
//
// Returns: array of { forecast_date, committed, rows } sorted ascending,
// where rows is { [row_key]: { eac, etc, actual, bac, commitment, method,
// manual_amount, qty, rate, committed } } — actual/bac/commitment are
// `null` on legacy (uncommitted) rows, signaling "read live data instead",
// exactly the same contract both screens already relied on.
// ─────────────────────────────────────────────────────────────────────────
// buildCostControlDataFromRelease() — historical Cost Control data for ONE
// Project Forecast Release. (ADD_Forecast_Architecture.md SS17/SS18.)
//
// ARCHITECTURAL RULE (explicitly agreed): this function performs NO
// business-logic calculation of its own. It is a thin orchestration layer
// with exactly three responsibilities:
//   1. Load the Direct and Indirect snapshot rows the Release references.
//   2. Reconstruct both historical hierarchies via
//      reconstructHierarchyFromSnapshotRows() — fail explicitly (never
//      silently recover) if reconstruction reports any structural error.
//   3. Adapt the reconstructed trees into the SAME output contract
//      buildCumulativeData() already returns, so CostControl.jsx requires
//      minimal changes to consume either a live or a historical dataset.
//
// This keeps the historical reporting pipeline (this function) completely
// independent from the live calculation pipeline (buildCumulativeData()) —
// there is still exactly one place BAC/Commitment/Actual/EAC aggregation
// logic lives (buildCumulativeData(), for live data; the frozen
// snapshot_* / eac_amount columns themselves, for historical data). This
// function never recomputes either — it only reads and reshapes what was
// already frozen at Commit time.
//
// A Release with only one source populated (SS17.3's agreed validation —
// at least one of direct/indirect, not necessarily both) simply produces
// an empty collection for the missing side (directRows: [] or
// indirectRows: []) — no warnings, no special-casing here; that belongs
// in the UI layer if the application wants to inform the user, per
// explicit agreement.
//
// NOTE ON FIELD COVERAGE: buildCumulativeData()'s live rows carry many
// fields (actualThisPeriod, actualPrevious, cashOut, the per-source-type
// inv* breakdowns, etc.) that a frozen forecast_history row simply does
// not store — those are live-only aggregation detail with no historical
// equivalent, confirmed Out of Scope for a Release under this
// conversation's Financial-State-vs-Presentation test (they're
// reporting/analysis derivatives, not part of the Cost Forecast's frozen
// financial state itself). earnedValue/cpi/accrual, however, ARE
// genuinely available — earnedValue is frozen into
// forecast_history.ev_amount at Commit time (a real persisted business
// value from the Budget module, exactly like eac_amount/snapshot_budget),
// accrual is frozen into snapshot_accrual, and cpi is derived from
// earnedValue/totalActualCost here, not fabricated. This function
// populates every field a Snapshot Layer row actually carries (bac,
// commitment, totalActualCost, eac, accrual, earnedValue; etc/cpi derived
// from those) and leaves only the confirmed-out-of-scope fields at their
// zero-value default.
export async function buildCostControlDataFromRelease(release) {
  const errors = []

  // Contract Value — Reference Metadata (per this conversation's finalized
  // model: reference data describing the project itself, not a piece of
  // financial state). It is never frozen into forecast_history and is
  // always read live from projects.contract_value, exactly as
  // buildCumulativeData() does — a Release changes which forecast/cost
  // state is shown, never which contract the project itself has.
  const { data: projRow } = release.project_id
    ? await supabase.from('projects').select('contract_value').eq('id', release.project_id).single()
    : { data: null }
  const contractValue = parseFloat(projRow?.contract_value) || 0

  async function loadAndReconstruct(snapshotId) {
    if (!snapshotId) return { roots: [], errors: [] }
    const { data } = await supabase
      .from('forecast_history')
      .select('row_key, sub_code, cost_code, parent_key, resource_type, eac_amount, etc_amount, ev_amount, snapshot_actual, snapshot_budget, snapshot_commitment, snapshot_accrual, snapshot_indirect_category_id, snapshot_indirect_category_name, is_forecasted, method')
      .eq('snapshot_id', snapshotId)
    return reconstructHierarchyFromSnapshotRows(data || [])
  }

  const directReconstruction = await loadAndReconstruct(release.direct_snapshot_id)
  const indirectReconstruction = await loadAndReconstruct(release.indirect_snapshot_id)

  // Fail explicitly on any structural inconsistency — never attempt to
  // recover or silently proceed with a partially-reconstructed tree
  // (ADD SS18, explicitly agreed: a Release is an immutable historical
  // record; structural inconsistencies are data corruption, not a
  // recoverable business scenario).
  if (directReconstruction.errors.length > 0) {
    errors.push(...directReconstruction.errors.map(e => ({ ...e, source: 'direct' })))
  }
  if (indirectReconstruction.errors.length > 0) {
    errors.push(...indirectReconstruction.errors.map(e => ({ ...e, source: 'indirect' })))
  }
  if (errors.length > 0) {
    throw new Error(
      `buildCostControlDataFromRelease: historical hierarchy reconstruction failed for release ${release.id} — ` +
      `${errors.length} row(s) reference a parent_key not present in their snapshot: ` +
      JSON.stringify(errors)
    )
  }

  // Adapt a reconstructed node tree (from reconstructHierarchyFromSnapshotRows'
  // generic nested { row_key, parent_key, ..., children } shape) into the
  // EXACT shape buildCumulativeData() produces — no calculation, purely a
  // reshape, but the reshape itself matters: buildCumulativeData()'s
  // contract is NOT a nested tree. For a Cost Code (top level):
  //   - resourceRows: the Resource-level rows (each with its own fields,
  //     no nested children of its own in this contract).
  //   - children: a FLAT array of every Sub Code under this Cost Code
  //     (both under a Resource and orphan), each carrying isUnderResource
  //     and parent_id (the id of its owning Resource, if any) so
  //     CostControl.jsx can re-group them for display and filter by tab.
  // This exact flat-with-linkage shape (not nesting) is what
  // CostControl.jsx's rendering code actually depends on
  // (parent.resourceRows.find(r => r.id === child.parent_id) etc.) —
  // reconstructHierarchyFromSnapshotRows() intentionally stays a generic
  // nested tree (SS18, reusable by any future historical consumer); this
  // function is the one place that reshapes it for THIS consumer's
  // specific contract, per the explicitly agreed responsibility split.
  function adaptLeaf(node) {
    return {
      id: node.row_key,
      code: node.row_key,
      description: node.sub_code || node.row_key,
      bac: parseFloat(node.snapshot_budget) || 0,
      commitment: parseFloat(node.snapshot_commitment) || 0,
      totalActualCost: parseFloat(node.snapshot_actual) || 0,
      eac: parseFloat(node.eac_amount) || 0,
      // accrual (ADD_Forecast_Architecture.md SS19.2b): read from the
      // frozen snapshot_accrual column — a presentation-fidelity field,
      // not a calculation input (totalActualCost above already includes
      // its contribution). Never fabricated as 0 where a real historical
      // breakdown existed.
      accrual: parseFloat(node.snapshot_accrual) || 0,
      actualTotal: 0, actualThisPeriod: 0, actualPrevious: 0,
      cashOut: 0, cashOutThisPeriod: 0, prevEac: 0,
      // earnedValue (per this conversation's conclusion): EV is a business
      // value already persisted by the Budget module
      // (direct_budget_ev/indirect_budget_ev) — not derived inside Cost
      // Control — so flattenForSnapshot() freezes it into ev_amount at
      // Commit time, exactly like eac_amount/snapshot_budget above. Read
      // here as-is, never recalculated.
      earnedValue: parseFloat(node.ev_amount) || 0,
      varianceEac: 0, varianceCompletion: (parseFloat(node.snapshot_budget) || 0) - (parseFloat(node.eac_amount) || 0),
      invMaterials: 0, invSubcontracts: 0, invEquipment: 0, invPettyCash: 0, invOthers: 0, invSalaries: 0,
      // cpi — same formula used everywhere else in this file
      // (earnedValue / totalActualCost), derived from the two
      // already-frozen figures above — never a separate stored/fabricated
      // value. totalActualCost here is snapshot_actual (this node's
      // frozen Total Actual Cost), matching the same denominator used
      // throughout buildCumulativeData()'s own cpi calculations.
      cpi: (parseFloat(node.ev_amount) && parseFloat(node.snapshot_actual)) ? (parseFloat(node.ev_amount) / parseFloat(node.snapshot_actual)) : 0,
      // indirect_category_id/name (ADD_Forecast_Architecture.md SS19.2c):
      // read directly from the frozen snapshot row — never from live
      // indirect_categories/cost_codes metadata. A Release must present
      // itself entirely from its own stored state (same "no live
      // fallback" principle already applied to resource_type).
      indirect_category_id: node.snapshot_indirect_category_id || null,
      indirect_category_name: node.snapshot_indirect_category_name || null,
    }
  }

  function adaptResource(node) {
    return {
      ...adaptLeaf(node),
      isResource: true,
      // resource_type (ADD_Forecast_Architecture.md SS19): read directly
      // from the frozen snapshot row — never from live resource_dictionary
      // metadata. A Release must present itself entirely from its own
      // stored state; no live fallback of any kind (explicitly agreed).
      resource_type: node.resource_type || null,
    }
  }

  function adaptCostCode(node) {
    const rawChildren = node.children || []
    const resourceNodes = rawChildren.filter(c => c.sub_code == null && c.parent_key != null)
    const orphanSubNodes = rawChildren.filter(c => !(c.sub_code == null && c.parent_key != null))

    const adaptedResources = resourceNodes.map(adaptResource)

    // Flat Sub Code list: every Sub Code under this Cost Code, whether it
    // sits under a Resource or is an orphan — matching
    // buildCumulativeData()'s subRowsWithGrouping contract exactly.
    const flatSubCodes = []
    for (const res of resourceNodes) {
      for (const sub of (res.children || [])) {
        flatSubCodes.push({ ...adaptLeaf(sub), isUnderResource: true, parent_id: res.row_key })
      }
    }
    for (const orphan of orphanSubNodes) {
      flatSubCodes.push({ ...adaptLeaf(orphan), isUnderResource: false, parent_id: null })
    }

    return {
      ...adaptLeaf(node),
      isResource: false,
      resource_type: null,
      resourceRows: adaptedResources,
      children: flatSubCodes,
    }
  }

  const directRows = directReconstruction.roots.map(adaptCostCode)
  const indirectRows = indirectReconstruction.roots.map(adaptCostCode)

  // Totals — same calcTotal shape buildCumulativeData() produces, applied
  // to the adapted rows. This is a pure summation of already-adapted
  // values, not a recalculation of business logic.
  const calcTotal = (arr) => {
    const acc = arr.reduce((acc, r) => {
      for (const k of ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac','varianceEac','varianceCompletion','invMaterials','invSubcontracts','invEquipment','invPettyCash','invOthers','invSalaries','earnedValue']) {
        acc[k] = (acc[k] || 0) + (r[k] || 0)
      }
      return acc
    }, {})
    // cpi — earnedValue is now genuinely stored (ev_amount, frozen from
    // direct_budget_ev/indirect_budget_ev at Commit time — see
    // adaptLeaf()/pushRow() above). The old "no earnedValue history
    // stored" fallback no longer applies; cpi is derived here from the
    // two already-summed figures above, same formula buildCumulativeData()
    // itself uses — never a separate stored/fabricated value.
    acc.cpi = (acc.earnedValue && acc.totalActualCost) ? (acc.earnedValue / acc.totalActualCost) : 0
    return acc
  }

  const directTotals = calcTotal(directRows)
  const indirectTotals = calcTotal(indirectRows)
  const grandTotals = calcTotal([directTotals, indirectTotals])

  // Resource-type totals — same pure summation pattern buildCumulativeData()
  // uses (SS15.3-style: aggregation only, no recalculation), now populated
  // from resource_type stored in the snapshot itself (SS19) rather than
  // left empty. Only Resource-level rows (isResource === true) carry a
  // resource_type; Cost Code and Sub Code rows correctly have none.
  const resourceTypeTotals = { material: {}, labour: {}, subcontract: {}, equipment: {} }
  function collectResourceTypeTotals(nodes) {
    for (const node of nodes) {
      if (node.isResource && node.resource_type && resourceTypeTotals[node.resource_type]) {
        const t = resourceTypeTotals[node.resource_type]
        for (const k of ['bac','commitment','actualTotal','actualThisPeriod','actualPrevious','accrual','totalActualCost','cashOut','cashOutThisPeriod','eac','prevEac','earnedValue']) {
          t[k] = (t[k] || 0) + (node[k] || 0)
        }
      }
      if (node.resourceRows?.length) collectResourceTypeTotals(node.resourceRows)
      if (node.children?.length) collectResourceTypeTotals(node.children)
    }
  }
  collectResourceTypeTotals(directRows)
  for (const rtype of Object.keys(resourceTypeTotals)) {
    // Same fix as calcTotal() above — earnedValue is genuinely available
    // now (ev_amount), so cpi is derived rather than fabricated as 0.
    const t = resourceTypeTotals[rtype]
    t.cpi = (t.earnedValue && t.totalActualCost) ? (t.earnedValue / t.totalActualCost) : 0
  }

  // Indirect Category grouping (ADD_Forecast_Architecture.md SS19.2c) —
  // mirrors buildCumulativeData()'s exact grouping rule
  // (indirectRows.filter(r => r.indirect_category_id === cat.id), with
  // any row lacking a valid category falling into "Uncategorized"). The
  // category LIST itself is derived here from the distinct
  // indirect_category_id/name values frozen on the rows — there is no
  // separate Snapshot of the indirect_categories table (SS19.2c), so the
  // set of categories a historical Release can show is exactly the set
  // that was actually assigned to its rows at Commit time, not today's
  // live indirect_categories table (which may have since gained, lost, or
  // renamed categories).
  const categoryMap = new Map() // id -> { id, name, rows: [] }
  const uncategorizedIndirectRows = []
  for (const row of indirectRows) {
    const catId = row.indirect_category_id
    if (!catId) { uncategorizedIndirectRows.push(row); continue }
    if (!categoryMap.has(catId)) {
      categoryMap.set(catId, { id: catId, name: row.indirect_category_name || 'Unnamed Category', rows: [] })
    }
    categoryMap.get(catId).rows.push(row)
  }
  const indirectCategories = Array.from(categoryMap.values()).map(cat => ({
    ...cat,
    totals: calcTotal(cat.rows),
  }))
  const uncategorizedTotals = calcTotal(uncategorizedIndirectRows)

  return {
    directRows, indirectRows, directTotals, indirectTotals, grandTotals,
    resourceTypeTotals,
    indirectCategories, uncategorizedIndirectRows, uncategorizedTotals,
    // meta — per this conversation's finalized model:
    //   - contractValue: Reference Metadata, always live (see above).
    //   - grossProfit/budgetProfit: Derived from Snapshot (frozen EAC/BAC)
    //     + Reference Metadata (contractValue) — same formula
    //     buildCumulativeData() itself uses, computed here rather than left
    //     at a fabricated 0.
    //   - cashInTotal/cashOutTotal/netCash: confirmed Out of Scope for a
    //     Release (not part of the Cost Forecast's financial state per the
    //     Financial-State-vs-Presentation test) — intentionally left at 0.
    //     This is a product decision, not a bug; see the Executive Report
    //     TODO list for Cash Position.
    meta: {
      contractValue,
      cashInTotal: 0, cashOutTotal: 0, netCash: 0,
      grossProfit: contractValue - (grandTotals.eac || 0),
      budgetProfit: contractValue - (grandTotals.bac || 0),
    },
  }
}


export async function loadForecastHistory(projectId, source) {
  const [{ data: snapshots }, { data: hist }] = await Promise.all([
    supabase.from('forecast_snapshots')
      .select('id, forecast_date, created_at, created_by, row_count')
      .eq('project_id', projectId).eq('source', source),
    supabase.from('forecast_history')
      .select('row_key, sub_code, cost_code, forecast_date, eac_amount, etc_amount, method, manual_amount, qty, rate, snapshot_id, snapshot_actual, snapshot_budget, snapshot_commitment, snapshot_inputs, is_forecasted')
      .eq('project_id', projectId).eq('source', source),
  ])

  // ─── Decision Layer flag resolution for pre-migration rows ────────────
  // (ADD_Forecast_Architecture.md Addendum SS14). forecast_history.is_forecasted
  // is authoritative for any row committed AFTER the is_forecasted column
  // existed — flattenForSnapshot()/save_draft()/commit_forecast_draft() now
  // write it correctly at the source. Rows committed BEFORE that migration
  // still have is_forecasted=false on the column itself (the column's own
  // DEFAULT, backfilled with no real classification — see Addendum SS14.7
  // step 1) — their correct historical classification lives only in the
  // separate, append-only forecast_history_decision_flags table (SS14.4),
  // populated once via INSERT, never by touching forecast_history itself.
  //
  // Rule per row: if a flag row exists in forecast_history_decision_flags
  // for this exact (snapshot_id, row_key), that flag is the correct value —
  // this is only ever true for pre-migration rows, since flattenForSnapshot()
  // never writes an entry into this table for new rows going forward. If no
  // flag row exists, the row's own is_forecasted column is authoritative
  // (either a post-migration row written correctly at Commit, or a legacy
  // snapshot_id IS NULL row, which was never in scope for the flags table —
  // see migration_04's WHERE snapshot_id IS NOT NULL).
  const snapshotIds = [...new Set((hist || []).map(r => r.snapshot_id).filter(id => id != null))]
  let flagRows = []
  if (snapshotIds.length) {
    const { data } = await supabase
      .from('forecast_history_decision_flags')
      .select('snapshot_id, row_key, is_forecasted')
      .in('snapshot_id', snapshotIds)
    flagRows = data || []
  }
  const flagByKey = {}
  for (const f of flagRows) {
    flagByKey[`${f.snapshot_id}::${f.row_key}`] = f.is_forecasted
  }
  function resolveIsForecasted(r) {
    const flagKey = r.snapshot_id != null ? `${r.snapshot_id}::${r.row_key}` : null
    if (flagKey != null && flagByKey[flagKey] !== undefined) return flagByKey[flagKey]
    return r.is_forecasted === true
  }

  const committedDates = new Set((snapshots || []).map(s => s.forecast_date))
  const snapshotMetaByDate = {}
  for (const s of (snapshots || [])) snapshotMetaByDate[s.forecast_date] = s

  const dateMap = {}
  for (const r of (hist || [])) {
    if (!dateMap[r.forecast_date]) dateMap[r.forecast_date] = {}
    const isCommitted = r.snapshot_id != null
    dateMap[r.forecast_date][r.row_key] = {
      eac: parseFloat(r.eac_amount) || 0,
      etc: r.etc_amount != null ? (parseFloat(r.etc_amount) || 0) : null,
      // null (not 0) on legacy rows — this is the explicit "no frozen value,
      // fall back to live data" signal every consumer already depends on.
      // Collapsing this to 0 would make a legacy period look like a real
      // zero Actual/Budget/Commitment instead of "unknown, use live".
      actual: isCommitted ? (parseFloat(r.snapshot_actual) || 0) : null,
      bac: isCommitted ? (parseFloat(r.snapshot_budget) || 0) : null,
      commitment: isCommitted ? (parseFloat(r.snapshot_commitment) || 0) : null,
      method: r.method,
      manual_amount: r.manual_amount,
      qty: r.qty,
      rate: r.rate,
      snapshot_inputs: r.snapshot_inputs || {},
      committed: isCommitted,
      // Decision Layer / Snapshot Layer discriminator (ADD SS4/SS5). Ownership
      // must be read from this field, never from row existence. Resolved via
      // resolveIsForecasted() above — column value for post-migration rows,
      // forecast_history_decision_flags lookup for pre-migration rows.
      is_forecasted: resolveIsForecasted(r),
    }
  }

  return Object.entries(dateMap)
    .map(([date, rows]) => ({
      forecast_date: date,
      committed: committedDates.has(date),
      snapshotMeta: snapshotMetaByDate[date] || null,
      rows,
    }))
    .sort((a, b) => a.forecast_date.localeCompare(b.forecast_date))
}

// ─── loadForecastSnapshot — reads a SINGLE committed period. Thin
// convenience wrapper around loadForecastHistory(); contains no snapshot-
// interpretation logic of its own, just filters that function's output down
// to one forecast_date and reshapes it into a flat rows array. Only usable
// for committed periods (snapshot_id set) — for legacy periods, callers
// should read straight from loadForecastHistory()'s per-period `committed:
// false` entries and fall back to live data, exactly as documented there.
export async function loadForecastSnapshot(projectId, source, forecastDate) {
  const all = await loadForecastHistory(projectId, source)
  const period = all.find(h => h.forecast_date === forecastDate && h.committed)
  if (!period) return { snapshot: null, rows: [] }

  const rows = Object.entries(period.rows).map(([row_key, r]) => ({
    row_key,
    sub_code: null, // not tracked per-row in loadForecastHistory's flat shape; callers needing sub_code/cost_code should read forecast_history directly via loadForecastHistory's raw rows if ever required
    cost_code: null,
    eac: r.eac,
    etc: r.etc != null ? r.etc : (r.eac - (r.actual || 0)),
    actual: r.actual || 0,
    bac: r.bac || 0,
    commitment: r.commitment || 0,
    method: r.method,
    manual_amount: r.manual_amount,
    qty: r.qty,
    rate: r.rate,
    snapshot_inputs: r.snapshot_inputs || {},
  }))

  return { snapshot: period.snapshotMeta, rows }
}



// ─── commitForecastSnapshot — the ONLY function that writes a new
// immutable forecast snapshot. Wraps the commit_forecast_snapshot RPC
// (single Postgres transaction — all-or-nothing) so neither
// DirectForecast.jsx nor IndirectForecast.jsx calls supabase.rpc() directly
// or handles the raw response shape itself.
// rows must come from flattenForSnapshot() — this function does no row
// preparation of its own.
export async function commitForecastSnapshot(projectId, source, forecastDate, rows) {
  const { data, error } = await supabase.rpc('commit_forecast_snapshot', {
    p_project_id: projectId,
    p_source: source,
    p_forecast_date: forecastDate,
    p_rows: rows,
  })
  if (error) throw error
  return data // the new snapshot_id
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2 — Forecast Draft / Revision Lifecycle
// ═══════════════════════════════════════════════════════════════════════════
// These functions manage WHEN and HOW a forecast becomes permanent — they
// contain zero financial calculation logic. Every number they carry (EAC,
// ETC, snapshot_actual/budget/commitment) was already computed by
// buildCumulativeData() and flattenForSnapshot() exactly as before; this
// layer only decides which database row that data belongs to (a mutable
// Draft, or a new immutable Revision) and never recalculates anything.
// buildCumulativeData() itself is completely unmodified by Phase 2.

// ─── getOrCreateDraft — the single entry point for "give me the current
// Draft". Returns the existing Draft's id if one exists (regardless of
// which month/year is passed — there is at most one Draft per
// project+source, per the DB-level partial unique index), or creates a new
// empty one for the given forecast period.
export async function getOrCreateDraft(projectId, source, forecastMonth, forecastYear) {
  const { data, error } = await supabase.rpc('get_or_create_draft', {
    p_project_id: projectId,
    p_source: source,
    p_forecast_month: forecastMonth,
    p_forecast_year: forecastYear,
  })
  if (error) throw error
  return data // draft snapshot_id
}

// ─── saveDraft — persists the current in-progress edits to the Draft in
// the database (full replace, not incremental — see save_draft's own
// documentation for why). Does NOT create a Revision; the Draft remains
// status='draft' and fully editable afterward. rows must come from
// flattenForSnapshot(), same as before.
// correlationId (optional, diagnostic-only): threaded through to the
// save_draft() RPC's RAISE LOG lines so a single Commit attempt's full
// React→Postgres trace can be reconstructed by searching logs for one id.
export async function saveDraft(draftId, rows, correlationId = null) {
  const { error } = await supabase.rpc('save_draft', {
    p_draft_id: draftId,
    p_rows: rows,
    p_correlation_id: correlationId,
  })
  if (error) throw error
}

// ─── commitForecastDraft — the ONLY way a Draft becomes a permanent,
// immutable Revision. Returns the new (distinct) committed snapshot_id —
// never the same id as the Draft, per the agreed "INSERT new, don't UPDATE
// the Draft in place" decision. After this call, a fresh empty Draft no
// longer exists automatically; callers should call getOrCreateDraft() again
// for the next editing cycle if needed.
// correlationId (optional, diagnostic-only): same purpose as in saveDraft().
export async function commitForecastDraft(draftId, notes = null, correlationId = null) {
  const { data, error } = await supabase.rpc('commit_forecast_draft', {
    p_draft_id: draftId,
    p_notes: notes,
    p_correlation_id: correlationId,
  })
  if (error) throw error
  return data // new committed snapshot_id
}

// ─── createRevisionFrom — starts a new editing cycle seeded from a chosen
// committed Revision's values. Throws if a Draft already exists for this
// project+source (at most one Draft at a time) — callers should catch this
// and prompt the user to commit or discard their current Draft first.
export async function createRevisionFrom(projectId, source, sourceSnapshotId, forecastMonth, forecastYear) {
  const { data, error } = await supabase.rpc('create_revision_from', {
    p_project_id: projectId,
    p_source: source,
    p_source_snapshot_id: sourceSnapshotId,
    p_forecast_month: forecastMonth,
    p_forecast_year: forecastYear,
  })
  if (error) throw error
  return data // new draft snapshot_id
}

// ─── discardDraft — deletes an uncommitted Draft entirely (rows + envelope).
// Only legal while status='draft' — the RPC itself refuses otherwise.
export async function discardDraft(draftId) {
  const { error } = await supabase.rpc('discard_draft', { p_draft_id: draftId })
  if (error) throw error
}

// ─── loadForecastVersions — the single shared data source for the Version
// Selector dropdown. Returns every version (the current Draft if one
// exists, plus every committed Revision) for a project+source, each
// labeled with everything the UI needs to render "✏️ Draft (Apr-2026)" /
// "🔒 Apr-2026 Rev.2 (Current)" / "🔒 Apr-2026 Rev.1" without the screen
// deriving any of that itself. "Current" per forecast_date is computed
// dynamically here (highest revision_number among committed rows for that
// date) — there is no revision_status column; this mirrors the
// forecast_current_revisions SQL view's own logic so both stay consistent.
export async function loadForecastVersions(projectId, source) {
  const { data: snapshots, error } = await supabase
    .from('forecast_snapshots')
    .select('id, status, forecast_date, revision_number, based_on_snapshot_id, notes, created_by, created_at, updated_at, row_count')
    .eq('project_id', projectId)
    .eq('source', source)
    .in('status', ['draft', 'committed'])
    .order('forecast_date', { ascending: false })
    .order('revision_number', { ascending: false })
  if (error) throw error

  const draft = (snapshots || []).find(s => s.status === 'draft') || null
  const committed = (snapshots || []).filter(s => s.status === 'committed')

  // "Current" = highest revision_number per forecast_date among committed rows.
  const currentIdByDate = {}
  for (const s of committed) {
    const key = s.forecast_date
    if (!currentIdByDate[key] || s.revision_number > (currentIdByDate[key].revision_number ?? -1)) {
      currentIdByDate[key] = s
    }
  }
  const currentIds = new Set(Object.values(currentIdByDate).map(s => s.id))

  return {
    draft,
    revisions: committed.map(s => ({ ...s, isCurrent: currentIds.has(s.id) })),
  }
}

// ─── By Month (Analytics Studio — uses actual_cost_date) ─────────────────────
export async function buildByMonthData(projectId) {
  if (!projectId) return []

  const [ipcRes, payrollRes, storeRes, pettyRes, accrualRes, costCodesRes, budgetRes, eacRes, commitRes] = await Promise.all([
    supabase.from('ipc_line_items')
      .select('sub_code,this_period_amount,actual_cost_date,ipc_certificates!inner(project_id,status)')
      .eq('ipc_certificates.project_id', projectId).eq('ipc_certificates.status', 'approved'),
    supabase.from('payroll_entries')
      .select('cost_code_id,total_salary,year,month,period_type,cost_codes(code,description)')
      .eq('project_id', projectId).eq('status', 'approved'),
    supabase.from('store_issues')
      .select('sub_code,amount,issued_date,cost_codes(code,description)')
      .eq('project_id', projectId),
    supabase.from('petty_cash_vouchers')
      .select('sub_code,budget_code,amount_exc_vat,actual_cost_date')
      .eq('project_id', projectId),
    supabase.from('accrual_entries')
      .select('cost_code_id,amount,actual_cost_date,cost_codes(code,description)')
      .eq('project_id', projectId),
    supabase.from('cost_codes').select('id,code,description,cost_type').eq('project_id', projectId).limit(5000),
    supabase.from('budget_summary').select('cost_code_id,total_budget').eq('project_id', projectId),
    supabase.from('forecast_eac').select('sub_code,cost_code_id,eac_amount,budget_row_id').eq('project_id', projectId),
    supabase.rpc('get_commitment_by_subcode', { p_project_id: projectId }),
  ])

  const ccMap = Object.fromEntries((costCodesRes.data||[]).map(c=>[c.id,c]))
  const codeMap = Object.fromEntries((costCodesRes.data||[]).map(c=>[c.code.toLowerCase(),c]))

  // period agg: { [period]: { actual, ipc, store, payroll, petty_cash, accrual, budget, eac, commitment } }
  const periodAgg = {}

  function ensurePeriod(p) {
    if (!periodAgg[p]) periodAgg[p] = { period:p, actual:0, ipc:0, store:0, payroll:0, petty_cash:0, accrual:0, budget:0, eac:0, commitment:0 }
  }

  // IPC
  for (const r of (ipcRes.data||[])) {
    if (!r.actual_cost_date) continue
    const p = r.actual_cost_date.slice(0,7)
    ensurePeriod(p)
    const amt = parseFloat(r.this_period_amount)||0
    periodAgg[p].ipc += amt; periodAgg[p].actual += amt
  }

  // Payroll
  for (const r of (payrollRes.data||[])) {
    const mo = parseInt(r.month)||1; const yr = parseInt(r.year)||new Date().getFullYear()
    const p = r.period_type === 'yearly' ? `${yr}-01` : `${yr}-${String(mo).padStart(2,'0')}`
    ensurePeriod(p)
    const amt = parseFloat(r.total_salary)||0
    periodAgg[p].payroll += amt; periodAgg[p].actual += amt
  }

  // Store
  for (const r of (storeRes.data||[])) {
    if (!r.issued_date) continue
    const p = r.issued_date.slice(0,7)
    ensurePeriod(p)
    const amt = parseFloat(r.amount)||0
    periodAgg[p].store += amt; periodAgg[p].actual += amt
  }

  // Petty Cash
  for (const r of (pettyRes.data||[])) {
    if (!r.actual_cost_date) continue
    const p = r.actual_cost_date.slice(0,7)
    ensurePeriod(p)
    const amt = parseFloat(r.amount_exc_vat)||0
    periodAgg[p].petty_cash += amt; periodAgg[p].actual += amt
  }

  // Accrual
  for (const r of (accrualRes.data||[])) {
    if (!r.actual_cost_date) continue
    const p = r.actual_cost_date.slice(0,7)
    ensurePeriod(p)
    const amt = parseFloat(r.amount)||0
    periodAgg[p].accrual += amt; periodAgg[p].actual += amt
  }

  // Budget (total — spread across first period or just as total)
  const totalBudget = (budgetRes.data||[]).reduce((s,r) => s+(parseFloat(r.total_budget)||0), 0)

  // EAC total
  const seenIds = new Set()
  let totalEac = 0
  for (const r of (eacRes.data||[])) {
    if (r.budget_row_id) { if (seenIds.has(r.budget_row_id)) continue; seenIds.add(r.budget_row_id) }
    totalEac += parseFloat(r.eac_amount)||0
  }

  // Commitment total
  const totalCommitment = (commitRes.data||[]).reduce((s,r) => s+(parseFloat(r.total)||0), 0)

  // Sort periods
  const sorted = Object.values(periodAgg).sort((a,b) => a.period.localeCompare(b.period))

  // Add cumulative actual + budget/eac/commitment on each row (for line charts)
  let cumActual = 0
  return sorted.map(r => {
    cumActual += r.actual
    return {
      ...r,
      name: r.period,
      cumulative_actual: cumActual,
      budget: totalBudget,
      eac: totalEac,
      commitment: totalCommitment,
    }
  })
}

// ─── By Cost Code (flat, for bar/table) ──────────────────────────────────────
export async function buildByCostCodeData(projectId) {
  const { directRows, indirectRows, grandTotals } = await buildCumulativeData(projectId, null, null)
  const all = [...directRows, ...indirectRows]
  return all.map(r => ({
    name: r.code,
    label: `${r.code} — ${r.description}`,
    cost_type: r.cost_type,
    budget: r.bac || 0,
    commitment: r.commitment || 0,
    actual: r.totalActualCost || 0,
    eac: r.eac || 0,
    cashout: r.cashOut || 0,
    var_comp: (r.bac||0) - (r.eac||0),
  }))
}